/**
 * Pure layout tools for moodboard slots (align / distribute / duplicate page).
 */

import polygonClipping from "polygon-clipping";
import {
  assignDefaultBoards,
  defaultBoardForPageIndex,
  normalizePageBoard,
} from "./skillAgentMoodboardBoard";
import {
  CLIP_MASK_MARK_TYPES,
  clampMark,
  clampSlot,
  clipPaintFromMark,
  cropKeepImageFixed,
  ensureLocalClip,
  knifeCutMarks,
  localClipToPage,
  markBounds,
  markOutlinePoints,
  markStrokeSvg,
  lerpHexColor,
  markPaintColors,
  nearestOnPolyline,
  nearestPolylineFraction,
  normalizeWidthPoints,
  samplePolylineAtT,
  widthScaleAtT,
  markAsSymbolTemplate,
  normalizeSymbolEntry,
  newGroupId,
  newMarkId,
  newSymbolId,
  newSlotId,
  normalizeFreePage,
  normalizePageChrome,
  normalizeRotation,
  normalizeSlotClip,
  pageClipToLocal,
  regularPolygonPoints,
  slotClipGeomKey,
  starPolygonPoints,
  textMarkBoxHeight,
  textMarkBoxWidth,
  translateMark,
} from "./skillAgentMoodboardLayout";

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function slotCenter(s) {
  return {
    cx: Number(s.x) + Number(s.w) / 2,
    cy: Number(s.y) + Number(s.h) / 2,
  };
}

/** Página = artboard VectorCraft (coords 0–1). */
const PAGE_BOUNDS = { x: 0, y: 0, w: 1, h: 1, cx: 0.5, cy: 0.5 };

function normalizeAlignTo(to) {
  const t = String(to || "selection").toLowerCase();
  if (t === "page" || t === "artboard") return "page";
  if (t === "key") return "key";
  return "selection";
}

function slotBox(s) {
  const x = Number(s.x) || 0;
  const y = Number(s.y) || 0;
  const w = Number(s.w) || 0;
  const h = Number(s.h) || 0;
  return { x, y, w, h, cx: x + w / 2, cy: y + h / 2 };
}

/**
 * Ancla de alineación (VectorCraft Align To).
 * to: selection | page | key — keyId fija el objeto clave (no se mueve).
 */
function alignAnchorFromBoxes(boxes, edge, to, keyId) {
  const mode = normalizeAlignTo(to);
  if (!boxes.length) return null;
  if (mode === "page") {
    if (edge === "left") return PAGE_BOUNDS.x;
    if (edge === "right") return PAGE_BOUNDS.x + PAGE_BOUNDS.w;
    if (edge === "top") return PAGE_BOUNDS.y;
    if (edge === "bottom") return PAGE_BOUNDS.y + PAGE_BOUNDS.h;
    if (edge === "hcenter") return PAGE_BOUNDS.cx;
    if (edge === "vcenter") return PAGE_BOUNDS.cy;
    return null;
  }
  if (mode === "key" && keyId != null) {
    const key = boxes.find((b) => String(b.id) === String(keyId));
    if (!key) return null;
    if (edge === "left") return key.x;
    if (edge === "right") return key.x + key.w;
    if (edge === "top") return key.y;
    if (edge === "bottom") return key.y + key.h;
    if (edge === "hcenter") return key.cx;
    if (edge === "vcenter") return key.cy;
    return null;
  }
  // selection
  if (edge === "left") return Math.min(...boxes.map((b) => b.x));
  if (edge === "right") return Math.max(...boxes.map((b) => b.x + b.w));
  if (edge === "top") return Math.min(...boxes.map((b) => b.y));
  if (edge === "bottom") return Math.max(...boxes.map((b) => b.y + b.h));
  if (edge === "hcenter") {
    const xs = boxes.map((b) => b.cx);
    return (Math.min(...xs) + Math.max(...xs)) / 2;
  }
  if (edge === "vcenter") {
    const ys = boxes.map((b) => b.cy);
    return (Math.min(...ys) + Math.max(...ys)) / 2;
  }
  return null;
}

/**
 * Align selected slots (coords 0–1).
 * edge: left|right|top|bottom|hcenter|vcenter
 * opts.to: selection|page|key (VectorCraft Align To)
 * opts.keyId: id del objeto clave (no se mueve)
 */
export function alignSlots(slots, selectedIds, edge, opts = {}) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(slots) ? slots : [];
  const selected = list.filter((s) => ids.has(String(s.id)));
  const to = normalizeAlignTo(opts.to);
  const keyId = opts.keyId != null ? String(opts.keyId) : null;
  const minN = to === "page" ? 1 : 2;
  if (selected.length < minN) return list;

  const boxes = selected.map((s) => ({ id: String(s.id), ...slotBox(s) }));
  const anchor = alignAnchorFromBoxes(boxes, edge, to, keyId);
  if (anchor == null || !Number.isFinite(anchor)) return list;

  return list.map((s) => {
    const id = String(s.id);
    if (!ids.has(id)) return s;
    if (to === "key" && keyId && id === keyId) return s;
    const next = { ...s };
    if (edge === "left") next.x = anchor;
    else if (edge === "right") next.x = anchor - (Number(s.w) || 0);
    else if (edge === "top") next.y = anchor;
    else if (edge === "bottom") next.y = anchor - (Number(s.h) || 0);
    else if (edge === "hcenter") next.x = anchor - (Number(s.w) || 0) / 2;
    else if (edge === "vcenter") next.y = anchor - (Number(s.h) || 0) / 2;
    else return s;
    return clampSlot(next);
  });
}

/**
 * Distribute selected slots evenly along an axis.
 * axis: "x" | "y"
 * opts.to: selection|page — page ancla extremos a 0/1 (VectorCraft artboard)
 * opts.spacing: gap fijo 0–1 (Distribute Spacing); con keyId el clave no se mueve
 * opts.keyId: objeto clave
 */
export function distributeSlots(slots, selectedIds, axis = "x", opts = {}) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(slots) ? slots : [];
  const to = normalizeAlignTo(opts.to);
  const keyId = opts.keyId != null ? String(opts.keyId) : null;
  const spacing =
    opts.spacing != null && Number.isFinite(Number(opts.spacing))
      ? Math.max(0, Number(opts.spacing))
      : null;
  const selected = list
    .filter((s) => ids.has(String(s.id)))
    .slice()
    .sort((a, b) =>
      axis === "y"
        ? (Number(a.y) || 0) - (Number(b.y) || 0)
        : (Number(a.x) || 0) - (Number(b.x) || 0),
    );
  const minN = spacing != null && keyId ? 2 : to === "page" ? 2 : 3;
  if (selected.length < minN) return list;

  const vert = axis === "y";
  const sizeOf = (s) => (vert ? Number(s.h) || 0 : Number(s.w) || 0);
  const posOf = (s) => (vert ? Number(s.y) || 0 : Number(s.x) || 0);

  let first;
  let last;
  let gap;

  if (spacing != null && keyId) {
    const keyIdx = selected.findIndex((s) => String(s.id) === keyId);
    if (keyIdx < 0) return list;
    const keyPos = posOf(selected[keyIdx]);
    const keySize = sizeOf(selected[keyIdx]);
    const pos = new Map();
    pos.set(keyId, keyPos);
    let cursor = keyPos + keySize + spacing;
    for (let i = keyIdx + 1; i < selected.length; i += 1) {
      pos.set(String(selected[i].id), cursor);
      cursor += sizeOf(selected[i]) + spacing;
    }
    cursor = keyPos - spacing;
    for (let i = keyIdx - 1; i >= 0; i -= 1) {
      cursor -= sizeOf(selected[i]);
      pos.set(String(selected[i].id), cursor);
      cursor -= spacing;
    }
    return list.map((s) => {
      if (!pos.has(String(s.id))) return s;
      return clampSlot(
        vert
          ? { ...s, y: pos.get(String(s.id)) }
          : { ...s, x: pos.get(String(s.id)) },
      );
    });
  }

  if (to === "page") {
    first = 0;
    last = 1;
  } else {
    first = posOf(selected[0]);
    last =
      posOf(selected[selected.length - 1]) +
      sizeOf(selected[selected.length - 1]);
  }
  const total = selected.reduce((acc, s) => acc + sizeOf(s), 0);
  gap =
    spacing != null ? spacing : (last - first - total) / (selected.length - 1);

  let cursor = first;
  const pos = new Map();
  selected.forEach((s) => {
    pos.set(String(s.id), cursor);
    cursor += sizeOf(s) + gap;
  });
  return list.map((s) => {
    if (!pos.has(String(s.id))) return s;
    return clampSlot(
      vert
        ? { ...s, y: pos.get(String(s.id)) }
        : { ...s, x: pos.get(String(s.id)) },
    );
  });
}

/** Duplicate a page (slots + marks + new board pose). */
/** Mesa libre debajo de `nearIndex` (sin solapar otras). */
export function nextBoardNearPages(pages, nearIndex = 0) {
  const list = Array.isArray(pages) ? pages : [];
  if (!list.length) return defaultBoardForPageIndex(0);
  const i = Math.max(
    0,
    Math.min(list.length - 1, Math.floor(Number(nearIndex) || 0)),
  );
  const board = normalizePageBoard(list[i]?.board, i);
  let tryBoard = {
    ...board,
    x: board.x,
    y: board.y + board.h + 0.15,
  };
  const used = new Set(
    list.map((p, idx) => {
      const b = normalizePageBoard(p.board, idx);
      return `${b.x.toFixed(2)},${b.y.toFixed(2)}`;
    }),
  );
  for (let n = 0; n < 40; n += 1) {
    const key = `${tryBoard.x.toFixed(2)},${tryBoard.y.toFixed(2)}`;
    if (!used.has(key)) break;
    tryBoard = {
      ...board,
      x: board.x,
      y: board.y + board.h + 0.15 + (n + 1) * (board.h + 0.15),
    };
  }
  return tryBoard;
}

export function duplicatePage(pages, pageIndex) {
  const list = assignDefaultBoards(
    (Array.isArray(pages) ? pages : []).map((p) => normalizeFreePage(p)),
  );
  const i = Math.max(
    0,
    Math.min(list.length - 1, Math.floor(Number(pageIndex) || 0)),
  );
  const src = list[i];
  if (!src) return list;

  const slots = (src.slots || []).map((s) =>
    clampSlot({
      ...clone(s),
      id: newSlotId("dup"),
    }),
  );
  const marks = (src.marks || []).map((m) => ({
    ...clone(m),
    id: newMarkId("dup"),
  }));
  const tryBoard = nextBoardNearPages(list, i);

  const chrome =
    src.chrome != null ? normalizePageChrome(src.chrome) : undefined;
  return [
    ...list,
    {
      slots,
      marks,
      board: tryBoard,
      ...(chrome ? { chrome } : null),
    },
  ];
}

/** Layer list for UI: slots + marks sorted by paint order. */
export function buildLayerEntries(page) {
  const p = normalizeFreePage(page);
  const slots = (p.slots || []).map((s, idx) => ({
    key: `slot:${s.id}`,
    kind: "slot",
    id: s.id,
    z: Number(s.z) || 0,
    label: s.assetId != null ? `Foto #${s.assetId}` : `Casilla ${idx + 1}`,
    assetId: s.assetId != null ? Number(s.assetId) : null,
  }));
  const marks = (p.marks || []).map((m, idx) => ({
    key: `mark:${m.id}`,
    kind: "mark",
    id: m.id,
    z: idx,
    label:
      m.type === "text"
        ? `Texto: ${String(m.text || "").slice(0, 24)}`
        : `Marca ${m.type || "path"}`,
    assetId: null,
  }));
  return [...slots, ...marks].sort((a, b) => {
    if (a.z !== b.z) return a.z - b.z;
    return String(a.key).localeCompare(String(b.key));
  });
}

export function bringSlotForward(slots, slotId) {
  const list = Array.isArray(slots) ? slots.map((s) => ({ ...s })) : [];
  const target = list.find((s) => String(s.id) === String(slotId));
  if (!target) return list;
  const maxZ = list.reduce((m, s) => Math.max(m, Number(s.z) || 0), 0);
  target.z = maxZ + 1;
  return list.map((s) => clampSlot(s));
}

export function sendSlotBackward(slots, slotId) {
  const list = Array.isArray(slots) ? slots.map((s) => ({ ...s })) : [];
  const target = list.find((s) => String(s.id) === String(slotId));
  if (!target) return list;
  const minZ = list.reduce(
    (m, s) => Math.min(m, Number(s.z) || 0),
    Number(target.z) || 0,
  );
  target.z = minZ - 1;
  return list.map((s) => clampSlot(s));
}

/** Rotate selected slots by delta degrees (CSS clockwise). */
export function rotateSlotsBy(slots, selectedIds, deltaDeg = 90) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(slots) ? slots : [];
  if (!ids.size) return list;
  const delta = Number(deltaDeg) || 0;
  return list.map((s) => {
    if (!ids.has(String(s.id))) return s;
    return clampSlot({
      ...s,
      rotation: normalizeRotation((Number(s.rotation) || 0) + delta),
    });
  });
}

/** Scale selected slots around each center. factor > 0. */
export function scaleSlotsBy(slots, selectedIds, factor = 1.1) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(slots) ? slots : [];
  const f = Number(factor);
  if (!ids.size || !Number.isFinite(f) || f <= 0) return list;
  return list.map((s) => {
    if (!ids.has(String(s.id))) return s;
    const { cx, cy } = slotCenter(s);
    const w = Math.max(0.02, (Number(s.w) || 0) * f);
    const h = Math.max(0.02, (Number(s.h) || 0) * f);
    return clampSlot({ ...s, w, h, x: cx - w / 2, y: cy - h / 2 });
  });
}

/** Mirror selected slots horizontally around the selection bbox center. */
export function reflectSlotsH(slots, selectedIds) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(slots) ? slots : [];
  const selected = list.filter((s) => ids.has(String(s.id)));
  if (!selected.length) return list;
  const left = Math.min(...selected.map((s) => Number(s.x) || 0));
  const right = Math.max(
    ...selected.map((s) => (Number(s.x) || 0) + (Number(s.w) || 0)),
  );
  const mid = (left + right) / 2;
  return list.map((s) => {
    if (!ids.has(String(s.id))) return s;
    const w = Number(s.w) || 0;
    const x = Number(s.x) || 0;
    return clampSlot({
      ...s,
      x: 2 * mid - (x + w),
      flipX: !s.flipX,
      rotation: normalizeRotation(-(Number(s.rotation) || 0)),
    });
  });
}

/** Rotate selected marks by delta degrees. */
export function rotateMarksBy(marks, selectedIds, deltaDeg = 90) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(marks) ? marks : [];
  if (!ids.size) return list;
  const delta = Number(deltaDeg) || 0;
  return list.map((m) => {
    if (!ids.has(String(m.id))) return m;
    return clampMark({
      ...m,
      rotation: normalizeRotation((Number(m.rotation) || 0) + delta),
    });
  });
}

/** Union AABB of marks (Free Transform). */
export function marksBBox(marks) {
  const list = Array.isArray(marks) ? marks : [];
  if (!list.length) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const m of list) {
    const b = markBounds(m);
    if (!b) continue;
    x0 = Math.min(x0, b.x);
    y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.w);
    y1 = Math.max(y1, b.y + b.h);
  }
  if (!Number.isFinite(x0)) return null;
  return {
    x: x0,
    y: y0,
    w: Math.max(0.001, x1 - x0),
    h: Math.max(0.001, y1 - y0),
  };
}

function mapMarkPoint(m, mapPt) {
  if (m.type === "path" || m.type === "textpath") {
    return clampMark({
      ...m,
      points: (m.points || []).map((p) => {
        const n = mapPt(Number(p.x) || 0, Number(p.y) || 0);
        return { ...p, x: n.x, y: n.y };
      }),
    });
  }
  if (m.type === "line" || m.type === "arrow" || m.type === "dimension") {
    const a = mapPt(Number(m.x1) || 0, Number(m.y1) || 0);
    const b = mapPt(Number(m.x2) || 0, Number(m.y2) || 0);
    return clampMark({ ...m, x1: a.x, y1: a.y, x2: b.x, y2: b.y });
  }
  if (m.type === "text") {
    // Misma lógica que rect: transformar la caja tipográfica, no solo el ancla.
    const x = Number(m.x) || 0;
    const y = Number(m.y) || 0;
    const w = textMarkBoxWidth(m);
    const h = textMarkBoxHeight(m);
    const tl = mapPt(x, y);
    const br = mapPt(x + w, y + h);
    return clampMark({
      ...m,
      x: Math.min(tl.x, br.x),
      y: Math.min(tl.y, br.y),
      w: Math.max(0.05, Math.abs(br.x - tl.x)),
      h: Math.max(0.02, Math.abs(br.y - tl.y)),
    });
  }
  const x = Number(m.x) || 0;
  const y = Number(m.y) || 0;
  const w = Number(m.w) || 0;
  const h = Number(m.h) || 0;
  const tl = mapPt(x, y);
  const br = mapPt(x + w, y + h);
  return clampMark({
    ...m,
    x: Math.min(tl.x, br.x),
    y: Math.min(tl.y, br.y),
    w: Math.max(0.008, Math.abs(br.x - tl.x)),
    h: Math.max(0.008, Math.abs(br.y - tl.y)),
  });
}

/** Scale marks from bbox0 → bbox1 (axis-aligned Free Transform). */
export function scaleMarksWithBBox(origins, bbox0, bbox1) {
  const list = Array.isArray(origins) ? origins : [];
  if (!bbox0 || !bbox1 || bbox0.w < 1e-6 || bbox0.h < 1e-6) return list;
  const sx = bbox1.w / bbox0.w;
  const sy = bbox1.h / bbox0.h;
  const sFont = Math.sqrt(Math.abs(sx * sy)) || 1;
  return list.map((m) => {
    if (m.type === "text") {
      const ow = textMarkBoxWidth(m);
      const oh = textMarkBoxHeight(m);
      const p = {
        x: bbox1.x + ((Number(m.x) || 0) - bbox0.x) * sx,
        y: bbox1.y + ((Number(m.y) || 0) - bbox0.y) * sy,
      };
      return clampMark({
        ...m,
        x: p.x,
        y: p.y,
        w: Math.max(0.05, ow * sx),
        h: Math.max(0.02, oh * sy),
        fontSize: Math.min(
          72,
          Math.max(8, Math.round((Number(m.fontSize) || 14) * sFont)),
        ),
      });
    }
    return mapMarkPoint(m, (px, py) => ({
      x: bbox1.x + (px - bbox0.x) * sx,
      y: bbox1.y + (py - bbox0.y) * sy,
    }));
  });
}

/** Rotate mark geometries around bbox center; also bumps mark.rotation. */
export function rotateMarksAroundBBox(origins, bbox0, deltaDeg) {
  const list = Array.isArray(origins) ? origins : [];
  if (!bbox0) return list;
  const cx = bbox0.x + bbox0.w / 2;
  const cy = bbox0.y + bbox0.h / 2;
  const rad = ((Number(deltaDeg) || 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const delta = Number(deltaDeg) || 0;
  return list.map((m) => {
    const next = mapMarkPoint(m, (px, py) => {
      const dx = px - cx;
      const dy = py - cy;
      return {
        x: cx + dx * cos - dy * sin,
        y: cy + dx * sin + dy * cos,
      };
    });
    return clampMark({
      ...next,
      rotation: normalizeRotation((Number(m.rotation) || 0) + delta),
    });
  });
}

/** Shear marks inside bbox (Alt+side handles). */
export function shearMarksInBBox(origins, bbox0, handle, dx, dy) {
  const list = Array.isArray(origins) ? origins : [];
  if (!bbox0) return list;
  const cx = bbox0.x + bbox0.w / 2;
  const cy = bbox0.y + bbox0.h / 2;
  const hw = Math.max(bbox0.w, 0.02);
  const hh = Math.max(bbox0.h, 0.02);
  let kx = 0;
  let ky = 0;
  if (handle === "e" || handle === "w") {
    kx = ((Number(dy) || 0) / hh) * (handle === "e" ? 1 : -1);
  } else if (handle === "n" || handle === "s") {
    ky = ((Number(dx) || 0) / hw) * (handle === "s" ? 1 : -1);
  }
  if (!kx && !ky) return list;
  return list.map((m) =>
    mapMarkPoint(m, (px, py) => ({
      x: px + (py - cy) * kx,
      y: py + (px - cx) * ky,
    })),
  );
}

/** Shear slots: desplazamiento X según (y - cy) * amount. */
export function shearSlotsBy(slots, selectedIds, amount = 0.15) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(slots) ? slots : [];
  const selected = list.filter((s) => ids.has(String(s.id)));
  if (!selected.length) return list;
  const a = Number(amount) || 0;
  const ys = selected.map((s) => slotCenter(s).cy);
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  return list.map((s) => {
    if (!ids.has(String(s.id))) return s;
    const { cy: scy } = slotCenter(s);
    const dx = (scy - cy) * a;
    return clampSlot({ ...s, x: (Number(s.x) || 0) + dx });
  });
}

/** Shear marks around selection center Y. */
export function shearMarksBy(marks, selectedIds, amount = 0.15) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(marks) ? marks : [];
  const selected = list.filter((m) => ids.has(String(m.id)));
  if (!selected.length) return list;
  const a = Number(amount) || 0;
  const ys = [];
  selected.forEach((m) => {
    if (m.type === "path" && Array.isArray(m.points)) {
      m.points.forEach((p) => ys.push(Number(p.y) || 0));
    } else if (m.type === "line" || m.type === "arrow") {
      ys.push(Number(m.y1) || 0, Number(m.y2) || 0);
    } else {
      ys.push((Number(m.y) || 0) + (Number(m.h) || 0) / 2);
    }
  });
  if (!ys.length) return list;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;

  return list.map((m) => {
    if (!ids.has(String(m.id))) return m;
    if (m.type === "path" && Array.isArray(m.points)) {
      return clampMark({
        ...m,
        points: m.points.map((p) => ({
          ...p,
          x: (Number(p.x) || 0) + ((Number(p.y) || 0) - cy) * a,
        })),
      });
    }
    if (m.type === "line" || m.type === "arrow") {
      return clampMark({
        ...m,
        x1: (Number(m.x1) || 0) + ((Number(m.y1) || 0) - cy) * a,
        x2: (Number(m.x2) || 0) + ((Number(m.y2) || 0) - cy) * a,
      });
    }
    if (m.type === "textpath" && Array.isArray(m.points)) {
      return clampMark({
        ...m,
        points: m.points.map((p) => ({
          ...p,
          x: (Number(p.x) || 0) + ((Number(p.y) || 0) - cy) * a,
        })),
      });
    }
    const y = (Number(m.y) || 0) + (Number(m.h) || 0) / 2;
    return clampMark({
      ...m,
      x: (Number(m.x) || 0) + (y - cy) * a,
    });
  });
}

/** Une dos paths si los extremos están cerca. Devuelve nueva lista o null. */
export function joinTwoPaths(marks, idA, idB, tol = 0.04) {
  const list = Array.isArray(marks) ? marks : [];
  const a = list.find((m) => String(m.id) === String(idA));
  const b = list.find((m) => String(m.id) === String(idB));
  if (!a || !b || a.type !== "path" || b.type !== "path") return null;
  const pa = (a.points || []).map((p) => ({
    x: Number(p.x) || 0,
    y: Number(p.y) || 0,
  }));
  const pb = (b.points || []).map((p) => ({
    x: Number(p.x) || 0,
    y: Number(p.y) || 0,
  }));
  if (pa.length < 2 || pb.length < 2) return null;

  const dist = (p, q) => Math.hypot(p.x - q.x, p.y - q.y);
  const ends = [
    { pts: [...pa, ...pb], d: dist(pa[pa.length - 1], pb[0]) },
    {
      pts: [...pa, ...[...pb].reverse()],
      d: dist(pa[pa.length - 1], pb[pb.length - 1]),
    },
    { pts: [...[...pa].reverse(), ...pb], d: dist(pa[0], pb[0]) },
    {
      pts: [...[...pa].reverse(), ...[...pb].reverse()],
      d: dist(pa[0], pb[pb.length - 1]),
    },
  ];
  ends.sort((x, y) => x.d - y.d);
  if (ends[0].d > tol) return null;
  const merged = clampMark({
    ...a,
    id: newMarkId(),
    points: ends[0].pts,
  });
  return list
    .filter((m) => String(m.id) !== String(idA) && String(m.id) !== String(idB))
    .concat([merged]);
}

function pathEndsMinDist(a, b) {
  const pa = (a?.points || []).map((p) => ({
    x: Number(p.x) || 0,
    y: Number(p.y) || 0,
  }));
  const pb = (b?.points || []).map((p) => ({
    x: Number(p.x) || 0,
    y: Number(p.y) || 0,
  }));
  if (pa.length < 2 || pb.length < 2) return Infinity;
  const d = (p, q) => Math.hypot(p.x - q.x, p.y - q.y);
  return Math.min(
    d(pa[pa.length - 1], pb[0]),
    d(pa[pa.length - 1], pb[pb.length - 1]),
    d(pa[0], pb[0]),
    d(pa[0], pb[pb.length - 1]),
  );
}

/**
 * Une ≥2 paths en cadena (greedy: el par de extremos más cercano cada vez).
 * Devuelve null si no puede unir al menos un par.
 */
export function joinPathChain(marks, selectedIds, tol = 0.04) {
  const list = Array.isArray(marks) ? marks : [];
  const ids = (selectedIds || []).map(String);
  if (ids.length < 2) return null;
  let working = list.slice();
  let pending = ids.filter((id) =>
    working.some((m) => String(m.id) === id && m.type === "path"),
  );
  if (pending.length < 2) return null;
  let joined = 0;
  while (pending.length >= 2) {
    let best = null;
    for (let i = 0; i < pending.length; i += 1) {
      for (let j = i + 1; j < pending.length; j += 1) {
        const ma = working.find((m) => String(m.id) === pending[i]);
        const mb = working.find((m) => String(m.id) === pending[j]);
        const score = pathEndsMinDist(ma, mb);
        if (score > tol) continue;
        if (!best || score < best.score) {
          best = { score, a: pending[i], b: pending[j] };
        }
      }
    }
    if (!best) break;
    const before = new Set(working.map((m) => String(m.id)));
    const trial = joinTwoPaths(working, best.a, best.b, tol);
    if (!trial) break;
    const created = trial.find((m) => !before.has(String(m.id)));
    if (!created) break;
    working = trial;
    pending = pending
      .filter((id) => id !== best.a && id !== best.b)
      .concat([String(created.id)]);
    joined += 1;
  }
  if (!joined) return null;
  return working;
}

/**
 * Convierte path → textpath (texto sobre el trazo).
 * `vertical: true` → Type on Path vertical (glifos upright / orientation).
 */
export function pathToTextPath(mark, text = "Text", { vertical = false } = {}) {
  if (!mark || mark.type !== "path") return null;
  const pts = Array.isArray(mark.points) ? mark.points : [];
  if (pts.length < 2) return null;
  return clampMark({
    type: "textpath",
    points: pts,
    text: String(text || "Text").slice(0, 500),
    color: mark.color,
    stroke: 0,
    fontSize: 14,
    fontFamily: "sans",
    ...(vertical ? { orientation: "vertical" } : null),
  });
}

/**
 * Índice de carácter bajo un clic en texto (aprox. por ancho de caja).
 * Touch Type VC: arrastrar glifo suelto.
 */
export function textCharIndexAt(mark, nx, ny) {
  if (!mark || mark.type !== "text") return -1;
  const text = String(mark.text || "");
  if (!text.length) return -1;
  const w = textMarkBoxWidth(mark);
  const h = textMarkBoxHeight(mark);
  const x = Number(mark.x) || 0;
  const y = Number(mark.y) || 0;
  const vertical =
    String(mark.writingMode || "").toLowerCase() === "vertical-rl";
  if (vertical) {
    if (nx < x - 0.02 || nx > x + w + 0.02) return -1;
    if (ny < y - 0.02 || ny > y + h + 0.02) return -1;
    const t = h > 1e-6 ? (ny - y) / h : 0;
    return Math.min(text.length - 1, Math.max(0, Math.floor(t * text.length)));
  }
  if (nx < x - 0.02 || nx > x + w + 0.02) return -1;
  if (ny < y - 0.02 || ny > y + h + 0.02) return -1;
  const t = w > 1e-6 ? (nx - x) / w : 0;
  return Math.min(text.length - 1, Math.max(0, Math.floor(t * text.length)));
}

/** Aplica dx/dy a un carácter (coords 0–1). */
export function applyTextCharOffset(mark, charIndex, dx, dy) {
  if (!mark || mark.type !== "text") return mark;
  const i = Math.round(Number(charIndex));
  const text = String(mark.text || "");
  if (i < 0 || i >= text.length) return mark;
  const list = Array.isArray(mark.charOffsets)
    ? mark.charOffsets.map((o) => ({ ...o }))
    : [];
  const idx = list.findIndex((o) => Number(o.i) === i);
  const next = {
    i,
    dx: Number(dx) || 0,
    dy: Number(dy) || 0,
  };
  if (Math.abs(next.dx) < 1e-5 && Math.abs(next.dy) < 1e-5) {
    return clampMark({
      ...mark,
      charOffsets: list.filter((o) => Number(o.i) !== i),
    });
  }
  if (idx >= 0) list[idx] = next;
  else list.push(next);
  return clampMark({ ...mark, charOffsets: list });
}

/** Mirror selected marks horizontally around selection bbox (approx via x/w or x1/x2). */
export function reflectMarksH(marks, selectedIds) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(marks) ? marks : [];
  const selected = list.filter((m) => ids.has(String(m.id)));
  if (!selected.length) return list;

  const xs = [];
  selected.forEach((m) => {
    if (m.type === "line" || m.type === "arrow") {
      xs.push(Number(m.x1) || 0, Number(m.x2) || 0);
    } else if (
      (m.type === "path" || m.type === "textpath") &&
      Array.isArray(m.points)
    ) {
      m.points.forEach((p) => xs.push(Number(p.x) || 0));
    } else {
      xs.push(Number(m.x) || 0);
      if (m.w != null) xs.push((Number(m.x) || 0) + (Number(m.w) || 0));
    }
  });
  if (!xs.length) return list;
  const mid = (Math.min(...xs) + Math.max(...xs)) / 2;

  return list.map((m) => {
    if (!ids.has(String(m.id))) return m;
    if (m.type === "line" || m.type === "arrow") {
      return clampMark({
        ...m,
        x1: 2 * mid - (Number(m.x1) || 0),
        x2: 2 * mid - (Number(m.x2) || 0),
      });
    }
    if (
      (m.type === "path" || m.type === "textpath") &&
      Array.isArray(m.points)
    ) {
      return clampMark({
        ...m,
        points: m.points.map((p) => ({
          ...p,
          x: 2 * mid - (Number(p.x) || 0),
        })),
      });
    }
    const x = Number(m.x) || 0;
    const w = Number(m.w) || 0;
    const next = {
      ...m,
      x: w > 0 ? 2 * mid - (x + w) : 2 * mid - x,
      rotation: normalizeRotation(-(Number(m.rotation) || 0)),
    };
    if (m.type === "arc" && Number.isFinite(Number(m.startAngle))) {
      next.startAngle = 180 - Number(m.startAngle);
      next.sweep = -(Number(m.sweep) || 90);
    }
    return clampMark(next);
  });
}

/** Mirror selected marks vertically around selection bbox. */
export function reflectMarksV(marks, selectedIds) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(marks) ? marks : [];
  const selected = list.filter((m) => ids.has(String(m.id)));
  if (!selected.length) return list;

  const ys = [];
  selected.forEach((m) => {
    if (m.type === "line" || m.type === "arrow") {
      ys.push(Number(m.y1) || 0, Number(m.y2) || 0);
    } else if (
      (m.type === "path" || m.type === "textpath") &&
      Array.isArray(m.points)
    ) {
      m.points.forEach((p) => ys.push(Number(p.y) || 0));
    } else {
      ys.push(Number(m.y) || 0);
      if (m.h != null) ys.push((Number(m.y) || 0) + (Number(m.h) || 0));
      else if (m.type === "text") ys.push((Number(m.y) || 0) + 0.04);
    }
  });
  if (!ys.length) return list;
  const mid = (Math.min(...ys) + Math.max(...ys)) / 2;

  return list.map((m) => {
    if (!ids.has(String(m.id))) return m;
    if (m.type === "line" || m.type === "arrow") {
      return clampMark({
        ...m,
        y1: 2 * mid - (Number(m.y1) || 0),
        y2: 2 * mid - (Number(m.y2) || 0),
      });
    }
    if (
      (m.type === "path" || m.type === "textpath") &&
      Array.isArray(m.points)
    ) {
      return clampMark({
        ...m,
        points: m.points.map((p) => ({
          ...p,
          y: 2 * mid - (Number(p.y) || 0),
        })),
      });
    }
    const y = Number(m.y) || 0;
    const h = Number(m.h) || 0;
    const next = {
      ...m,
      y: h > 0 ? 2 * mid - (y + h) : 2 * mid - y,
      rotation: normalizeRotation(-(Number(m.rotation) || 0)),
    };
    if (m.type === "arc" && Number.isFinite(Number(m.startAngle))) {
      next.startAngle = -Number(m.startAngle);
      next.sweep = -(Number(m.sweep) || 90);
    }
    return clampMark(next);
  });
}

/** Mirror selected slots vertically. */
export function reflectSlotsV(slots, selectedIds) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(slots) ? slots : [];
  const selected = list.filter((s) => ids.has(String(s.id)));
  if (!selected.length) return list;
  const ys = [];
  selected.forEach((s) => {
    ys.push(Number(s.y) || 0, (Number(s.y) || 0) + (Number(s.h) || 0));
  });
  const mid = (Math.min(...ys) + Math.max(...ys)) / 2;
  return list.map((s) => {
    if (!ids.has(String(s.id))) return s;
    const y = Number(s.y) || 0;
    const h = Number(s.h) || 0;
    return clampSlot({
      ...s,
      y: 2 * mid - (y + h),
      flipY: !s.flipY,
      rotation: normalizeRotation(-(Number(s.rotation) || 0)),
    });
  });
}

/** Bring selected marks to front (end of array = top). */
export function bringMarksToFront(marks, selectedIds) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(marks) ? marks : [];
  if (!ids.size) return list;
  const keep = [];
  const move = [];
  list.forEach((m) => {
    if (ids.has(String(m.id))) move.push(m);
    else keep.push(m);
  });
  if (!move.length) return list;
  return keep.concat(move);
}

/**
 * Knife sobre fotos/casillas: parte en 2 sin deformar.
 * Mismo zoom; pan anclado para continuidad visual (cropKeepImageFixed).
 */
export function knifeCutSlots(slots, knifePoints) {
  const knife = Array.isArray(knifePoints) ? knifePoints : [];
  const list = Array.isArray(slots) ? slots : [];
  if (knife.length < 2) return list;

  const k0 = knife[0];
  const k1 = knife[knife.length - 1];
  const kdx = (Number(k1.x) || 0) - (Number(k0.x) || 0);
  const kdy = (Number(k1.y) || 0) - (Number(k0.y) || 0);
  // Trazo más horizontal → corte horizontal (arriba/abajo); más vertical → izq/der
  const splitVertical = Math.abs(kdx) < Math.abs(kdy);

  const pointInSlot = (s, px, py) => {
    const x = Number(s.x) || 0;
    const y = Number(s.y) || 0;
    const w = Number(s.w) || 0;
    const h = Number(s.h) || 0;
    return px >= x && px <= x + w && py >= y && py <= y + h;
  };

  const midInside = (s) => {
    const hits = knife.filter((p) =>
      pointInSlot(s, Number(p.x) || 0, Number(p.y) || 0),
    );
    if (hits.length) {
      const sx = hits.reduce((a, p) => a + (Number(p.x) || 0), 0) / hits.length;
      const sy = hits.reduce((a, p) => a + (Number(p.y) || 0), 0) / hits.length;
      return { x: sx, y: sy };
    }
    const x = Number(s.x) || 0;
    const y = Number(s.y) || 0;
    const w = Number(s.w) || 0;
    const h = Number(s.h) || 0;
    const crosses =
      Math.min(Number(k0.x) || 0, Number(k1.x) || 0) <= x + w &&
      Math.max(Number(k0.x) || 0, Number(k1.x) || 0) >= x &&
      Math.min(Number(k0.y) || 0, Number(k1.y) || 0) <= y + h &&
      Math.max(Number(k0.y) || 0, Number(k1.y) || 0) >= y;
    if (!crosses) return null;
    return {
      x: Math.min(
        x + w,
        Math.max(x, ((Number(k0.x) || 0) + (Number(k1.x) || 0)) / 2),
      ),
      y: Math.min(
        y + h,
        Math.max(y, ((Number(k0.y) || 0) + (Number(k1.y) || 0)) / 2),
      ),
    };
  };

  const out = [];
  let any = false;
  list.forEach((s) => {
    const mid = midInside(s);
    if (!mid) {
      out.push(s);
      return;
    }
    const x = Number(s.x) || 0;
    const y = Number(s.y) || 0;
    const w = Number(s.w) || 0;
    const h = Number(s.h) || 0;
    const origBox = { x, y, w, h };
    const crop0 = {
      zoom: Number(s.zoom) || 1,
      panX: Number.isFinite(Number(s.panX)) ? Number(s.panX) : 0.5,
      panY: Number.isFinite(Number(s.panY)) ? Number(s.panY) : 0.5,
    };

    if (splitVertical) {
      let t = (mid.x - x) / (w || 1);
      t = Math.min(0.85, Math.max(0.15, t));
      const w1 = w * t;
      const w2 = w - w1;
      if (w1 < 0.04 || w2 < 0.04) {
        out.push(s);
        return;
      }
      const boxA = { x, y, w: w1, h };
      const boxB = { x: x + w1, y, w: w2, h };
      const cropA = cropKeepImageFixed(origBox, boxA, crop0, "x");
      const cropB = cropKeepImageFixed(origBox, boxB, crop0, "x");
      any = true;
      out.push(
        clampSlot({
          ...s,
          w: w1,
          zoom: cropA.zoom,
          panX: cropA.panX,
          panY: cropA.panY,
        }),
        clampSlot({
          ...s,
          id: newSlotId(),
          x: x + w1,
          w: w2,
          zoom: cropB.zoom,
          panX: cropB.panX,
          panY: cropB.panY,
          z: (Number(s.z) || 0) + 1,
        }),
      );
    } else {
      let t = (mid.y - y) / (h || 1);
      t = Math.min(0.85, Math.max(0.15, t));
      const h1 = h * t;
      const h2 = h - h1;
      if (h1 < 0.04 || h2 < 0.04) {
        out.push(s);
        return;
      }
      const boxA = { x, y, w, h: h1 };
      const boxB = { x, y: y + h1, w, h: h2 };
      const cropA = cropKeepImageFixed(origBox, boxA, crop0, "y");
      const cropB = cropKeepImageFixed(origBox, boxB, crop0, "y");
      any = true;
      out.push(
        clampSlot({
          ...s,
          h: h1,
          zoom: cropA.zoom,
          panX: cropA.panX,
          panY: cropA.panY,
        }),
        clampSlot({
          ...s,
          id: newSlotId(),
          y: y + h1,
          h: h2,
          zoom: cropB.zoom,
          panX: cropB.panX,
          panY: cropB.panY,
          z: (Number(s.z) || 0) + 1,
        }),
      );
    }
  });
  return any ? out : list;
}

/** Send selected marks to back (start of array = bottom). */
export function sendMarksToBack(marks, selectedIds) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(marks) ? marks : [];
  if (!ids.size) return list;
  const keep = [];
  const move = [];
  list.forEach((m) => {
    if (ids.has(String(m.id))) move.push(m);
    else keep.push(m);
  });
  if (!move.length) return list;
  return move.concat(keep);
}

/**
 * Align selected marks.
 * edge: left|right|top|bottom|hcenter|vcenter
 * opts.to: selection|page|key · opts.keyId
 */
export function alignMarks(marks, selectedIds, edge, opts = {}) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(marks) ? marks : [];
  const to = normalizeAlignTo(opts.to);
  const keyId = opts.keyId != null ? String(opts.keyId) : null;
  const selected = list.filter((m) => ids.has(String(m.id)));
  const minN = to === "page" ? 1 : 2;
  if (selected.length < minN) return list;
  const bounds = selected
    .map((m) => {
      const b = markBounds(m);
      return b ? { id: String(m.id), m, ...b } : null;
    })
    .filter(Boolean);
  if (bounds.length < minN) return list;

  const anchor = alignAnchorFromBoxes(bounds, edge, to, keyId);
  if (anchor == null || !Number.isFinite(anchor)) return list;

  const dxMap = new Map();
  const dyMap = new Map();
  bounds.forEach((b) => {
    if (to === "key" && keyId && b.id === keyId) {
      dxMap.set(b.id, 0);
      dyMap.set(b.id, 0);
      return;
    }
    let dx = 0;
    let dy = 0;
    if (edge === "left") dx = anchor - b.x;
    else if (edge === "right") dx = anchor - (b.x + b.w);
    else if (edge === "top") dy = anchor - b.y;
    else if (edge === "bottom") dy = anchor - (b.y + b.h);
    else if (edge === "hcenter") dx = anchor - b.cx;
    else if (edge === "vcenter") dy = anchor - b.cy;
    dxMap.set(b.id, dx);
    dyMap.set(b.id, dy);
  });

  return list.map((m) => {
    if (!ids.has(String(m.id))) return m;
    return translateMark(
      m,
      dxMap.get(String(m.id)) || 0,
      dyMap.get(String(m.id)) || 0,
    );
  });
}

/**
 * Distribute selected marks evenly.
 * axis: "x" | "y"
 * opts.to / opts.spacing / opts.keyId — igual que distributeSlots
 */
export function distributeMarks(marks, selectedIds, axis = "x", opts = {}) {
  const ids = new Set((selectedIds || []).map((id) => String(id)));
  const list = Array.isArray(marks) ? marks : [];
  const to = normalizeAlignTo(opts.to);
  const keyId = opts.keyId != null ? String(opts.keyId) : null;
  const spacing =
    opts.spacing != null && Number.isFinite(Number(opts.spacing))
      ? Math.max(0, Number(opts.spacing))
      : null;
  const selected = list
    .filter((m) => ids.has(String(m.id)))
    .map((m) => ({ m, b: markBounds(m) }))
    .filter((x) => x.b)
    .sort((a, b) => (axis === "y" ? a.b.y - b.b.y : a.b.x - b.b.x));
  const minN = spacing != null && keyId ? 2 : to === "page" ? 2 : 3;
  if (selected.length < minN) return list;

  const vert = axis === "y";
  const sizeOf = (x) => (vert ? x.b.h : x.b.w);
  const posOf = (x) => (vert ? x.b.y : x.b.x);

  if (spacing != null && keyId) {
    const keyIdx = selected.findIndex((x) => String(x.m.id) === keyId);
    if (keyIdx < 0) return list;
    const keyPos = posOf(selected[keyIdx]);
    const keySize = sizeOf(selected[keyIdx]);
    const delta = new Map();
    delta.set(keyId, 0);
    let cursor = keyPos + keySize + spacing;
    for (let i = keyIdx + 1; i < selected.length; i += 1) {
      const { m, b } = selected[i];
      const target = cursor;
      delta.set(String(m.id), vert ? target - b.y : target - b.x);
      cursor += sizeOf(selected[i]) + spacing;
    }
    cursor = keyPos - spacing;
    for (let i = keyIdx - 1; i >= 0; i -= 1) {
      cursor -= sizeOf(selected[i]);
      const { m, b } = selected[i];
      delta.set(String(m.id), vert ? cursor - b.y : cursor - b.x);
      cursor -= spacing;
    }
    return list.map((m) => {
      if (!delta.has(String(m.id))) return m;
      const d = delta.get(String(m.id));
      return translateMark(m, vert ? 0 : d, vert ? d : 0);
    });
  }

  let first;
  let last;
  if (to === "page") {
    first = 0;
    last = 1;
  } else {
    first = posOf(selected[0]);
    last =
      posOf(selected[selected.length - 1]) +
      sizeOf(selected[selected.length - 1]);
  }
  const total = selected.reduce((acc, x) => acc + sizeOf(x), 0);
  const gap =
    spacing != null ? spacing : (last - first - total) / (selected.length - 1);

  let cursor = first;
  const delta = new Map();
  selected.forEach(({ m, b }) => {
    const target = cursor;
    delta.set(String(m.id), vert ? target - b.y : target - b.x);
    cursor += sizeOf({ b }) + gap;
  });
  return list.map((m) => {
    if (!delta.has(String(m.id))) return m;
    const d = delta.get(String(m.id));
    return translateMark(m, vert ? 0 : d, vert ? d : 0);
  });
}

function clamp01(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

/**
 * Ids del mismo grupo que `seedId` (incluye el propio).
 * Sin groupId → solo [seedId].
 */
export function expandSelectionByGroup(marks, seedId) {
  const list = Array.isArray(marks) ? marks : [];
  const sid = String(seedId);
  const seed = list.find((m) => String(m.id) === sid);
  if (!seed) return new Set([sid]);
  const gid = seed.groupId != null ? String(seed.groupId).trim() : "";
  if (!gid) return new Set([sid]);
  const out = new Set();
  list.forEach((m) => {
    if (m.groupId != null && String(m.groupId).trim() === gid) {
      out.add(String(m.id));
    }
  });
  if (!out.size) out.add(sid);
  return out;
}

/**
 * Agrupa marcas seleccionadas (Object → Group / Ctrl+G).
 * Requiere ≥2 ids. Devuelve null si no aplica.
 */
export function groupMarks(marks, ids) {
  const list = Array.isArray(marks) ? marks : [];
  const idSet = new Set((ids || []).map(String));
  if (idSet.size < 2) return null;
  const targets = list.filter((m) => idSet.has(String(m.id)));
  if (targets.length < 2) return null;
  const groupId = newGroupId();
  return list.map((m) =>
    idSet.has(String(m.id)) ? clampMark({ ...m, groupId }) : m,
  );
}

/**
 * Desagrupa: quita groupId de todos los miembros de los grupos
 * tocados por la selección (Object → Ungroup / Ctrl+Shift+G).
 */
export function ungroupMarks(marks, ids) {
  const list = Array.isArray(marks) ? marks : [];
  const idSet = new Set((ids || []).map(String));
  if (!idSet.size) return null;
  const gids = new Set();
  list.forEach((m) => {
    if (
      idSet.has(String(m.id)) &&
      m.groupId != null &&
      String(m.groupId).trim()
    ) {
      gids.add(String(m.groupId).trim());
    }
  });
  if (!gids.size) return null;
  let changed = false;
  const next = list.map((m) => {
    if (m.groupId == null || !gids.has(String(m.groupId).trim())) return m;
    changed = true;
    const { groupId: _drop, ...rest } = m;
    return clampMark(rest);
  });
  return changed ? next : null;
}

/** Convierte forma geométrica → path (para liquify / cuts). */
export function markAsPath(mark) {
  if (!mark) return null;
  if (
    mark.type === "path" &&
    Array.isArray(mark.points) &&
    mark.points.length >= 2
  ) {
    return mark;
  }
  const outline = markOutlinePoints(mark);
  if (!outline || outline.length < 2) return null;
  return {
    ...mark,
    type: "path",
    points: outline.map((p) => ({ x: Number(p.x) || 0, y: Number(p.y) || 0 })),
    closed: true,
  };
}

/**
 * Pines automáticos VC: centro + extremos del bbox (o del path).
 * Coords en reposo (0–1).
 */
export function defaultPuppetPins(mark) {
  const asPath = markAsPath(mark);
  const b = markBounds(asPath || mark);
  if (!b) return [];
  const pins = [
    { rest: { x: b.cx, y: b.cy }, at: { x: b.cx, y: b.cy } },
    { rest: { x: b.x, y: b.cy }, at: { x: b.x, y: b.cy } },
    { rest: { x: b.x + b.w, y: b.cy }, at: { x: b.x + b.w, y: b.cy } },
  ];
  if (asPath?.points?.length >= 4) {
    const pts = asPath.points;
    const mid = pts[Math.floor(pts.length / 2)];
    pins.push({
      rest: { x: Number(mid.x) || b.cx, y: Number(mid.y) || b.cy },
      at: { x: Number(mid.x) || b.cx, y: Number(mid.y) || b.cy },
    });
  }
  return pins;
}

/** Normaliza lista de pines Puppet. */
export function normalizePuppetPins(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p) => {
      if (!p || typeof p !== "object") return null;
      const rx = Number(p.rest?.x ?? p.x);
      const ry = Number(p.rest?.y ?? p.y);
      const ax = Number(p.at?.x ?? p.rest?.x ?? p.x);
      const ay = Number(p.at?.y ?? p.rest?.y ?? p.y);
      if (![rx, ry, ax, ay].every(Number.isFinite)) return null;
      return {
        rest: { x: rx, y: ry },
        at: { x: ax, y: ay },
      };
    })
    .filter(Boolean)
    .slice(0, 24);
}

/**
 * Deforma puntos desde reposo con IDW de desplazamientos de pines
 * (aprox. ARAP de VC: drag pin → warp rígido-ish local).
 */
export function deformPointsWithPuppetPins(restPoints, pins) {
  const pts = Array.isArray(restPoints) ? restPoints : [];
  const list = normalizePuppetPins(pins);
  if (!pts.length || list.length < 1) {
    return pts.map((p) => ({ x: Number(p.x) || 0, y: Number(p.y) || 0 }));
  }
  const moved = list.some(
    (p) => Math.hypot(p.at.x - p.rest.x, p.at.y - p.rest.y) > 1e-6,
  );
  if (!moved) {
    return pts.map((p) => ({ x: Number(p.x) || 0, y: Number(p.y) || 0 }));
  }
  return pts.map((p) => {
    const x = Number(p.x) || 0;
    const y = Number(p.y) || 0;
    let sumW = 0;
    let sumDx = 0;
    let sumDy = 0;
    list.forEach((pin) => {
      const d2 =
        (x - pin.rest.x) * (x - pin.rest.x) +
        (y - pin.rest.y) * (y - pin.rest.y);
      const w = 1 / (d2 + 1e-5);
      sumW += w;
      sumDx += w * (pin.at.x - pin.rest.x);
      sumDy += w * (pin.at.y - pin.rest.y);
    });
    if (sumW < 1e-12) return { x, y };
    return { x: x + sumDx / sumW, y: y + sumDy / sumW };
  });
}

/**
 * Asegura rest shape + pines. Si no hay, crea pines auto.
 * Devuelve marca path con puppet: { restPoints, pins }.
 */
export function ensurePuppetState(mark, pinsOverride = null) {
  if (!mark || mark.type === "text" || mark.type === "textpath") return null;
  const asPath = markAsPath(mark);
  if (!asPath || !asPath.points?.length) return null;
  const existing = mark.puppet;
  const restPoints =
    Array.isArray(existing?.restPoints) && existing.restPoints.length >= 2
      ? existing.restPoints.map((p) => ({
          x: Number(p.x) || 0,
          y: Number(p.y) || 0,
        }))
      : asPath.points.map((p) => ({
          x: Number(p.x) || 0,
          y: Number(p.y) || 0,
        }));
  let pins = normalizePuppetPins(
    pinsOverride != null ? pinsOverride : existing?.pins,
  );
  if (!pins.length) pins = defaultPuppetPins({ ...asPath, points: restPoints });
  const points = deformPointsWithPuppetPins(restPoints, pins);
  return clampMark({
    ...asPath,
    id: mark.id,
    type: "path",
    closed: Boolean(asPath.closed ?? mark.closed ?? true),
    points,
    puppet: { restPoints, pins },
  });
}

/**
 * Mueve un pin (por índice) y re-deforma desde reposo.
 */
export function movePuppetPin(mark, pinIndex, atX, atY) {
  const state = ensurePuppetState(mark);
  if (!state?.puppet?.pins?.length) return mark;
  const pins = state.puppet.pins.map((p, i) =>
    i === pinIndex
      ? {
          rest: { ...p.rest },
          at: { x: Number(atX) || 0, y: Number(atY) || 0 },
        }
      : p,
  );
  return ensurePuppetState(state, pins);
}

/** Añade pin en (x,y) sobre la forma (rest = posición actual mapeada aprox.). */
export function addPuppetPin(mark, x, y) {
  const state = ensurePuppetState(mark);
  if (!state?.puppet) return mark;
  const pins = [
    ...state.puppet.pins,
    {
      rest: { x: Number(x) || 0, y: Number(y) || 0 },
      at: { x: Number(x) || 0, y: Number(y) || 0 },
    },
  ].slice(0, 24);
  return ensurePuppetState(state, pins);
}

/** Estilos Envelope › Make with Warp (VC / Illustrator). */
export const ENVELOPE_WARP_STYLES = [
  "arc",
  "arcLower",
  "arcUpper",
  "arch",
  "bulge",
  "shellLower",
  "shellUpper",
  "flag",
  "wave",
  "fish",
  "rise",
  "fisheye",
  "inflate",
  "squeeze",
  "twist",
];

/**
 * Desplazamiento (du,dv) en espacio bbox [0,1]² para un estilo warp.
 * bendAmt ∈ [-1,1]; hDist/vDist ∈ [-1,1] (distorsión).
 */
export function envelopeWarpOffset(
  style,
  u,
  v,
  bendAmt = 0.5,
  hDist = 0,
  vDist = 0,
) {
  const b = Math.max(-1, Math.min(1, Number(bendAmt) || 0));
  const hd = Math.max(-1, Math.min(1, Number(hDist) || 0));
  const vd = Math.max(-1, Math.min(1, Number(vDist) || 0));
  const uu = Math.max(0, Math.min(1, Number(u) || 0));
  const vv = Math.max(0, Math.min(1, Number(v) || 0));
  const s = String(style || "arc").toLowerCase();
  const sin1 = Math.sin(Math.PI * uu);
  const sin2 = Math.sin(2 * Math.PI * uu);
  const cos1 = Math.cos(Math.PI * uu);
  let du = hd * (uu - 0.5) * 0.35;
  let dv = vd * (vv - 0.5) * 0.35;
  if (s === "arc" || s === "arch") {
    dv += b * sin1 * (s === "arch" ? 0.85 : 1);
  } else if (s === "arclower") {
    dv += b * sin1 * (1 - vv);
  } else if (s === "arcupper") {
    dv += b * sin1 * vv;
  } else if (s === "bulge") {
    dv += b * sin1 * Math.sin(Math.PI * vv);
  } else if (s === "shelllower") {
    dv += b * Math.sin(Math.PI * uu * 0.5) * (1 - vv);
  } else if (s === "shellupper") {
    dv += b * Math.sin(Math.PI * (1 - uu) * 0.5) * vv;
  } else if (s === "flag") {
    dv += b * sin2 * 0.55;
  } else if (s === "wave") {
    dv += b * sin2 * Math.sin(Math.PI * vv) * 0.7;
  } else if (s === "fish") {
    dv += b * Math.sin(Math.PI * uu) * (0.35 + 0.65 * Math.sin(Math.PI * vv));
    du += b * 0.15 * cos1 * (vv - 0.5);
  } else if (s === "rise") {
    dv += b * uu * uu * 0.9;
  } else if (s === "fisheye" || s === "inflate" || s === "squeeze") {
    const dx = uu - 0.5;
    const dy = vv - 0.5;
    const r = Math.hypot(dx, dy) || 1e-6;
    const sign = s === "squeeze" ? -1 : 1;
    const f = sign * b * 0.55 * (s === "fisheye" ? 1 - r : r);
    du += (dx / r) * f;
    dv += (dy / r) * f;
  } else if (s === "twist") {
    const dx = uu - 0.5;
    const dy = vv - 0.5;
    const ang = b * Math.PI * Math.hypot(dx, dy);
    const c = Math.cos(ang);
    const sn = Math.sin(ang);
    du += dx * c - dy * sn - dx;
    dv += dx * sn + dy * c - dy;
  } else {
    dv += b * sin1;
  }
  return { du, dv };
}

/**
 * Object › Envelope Distort › Make with Warp (destructivo → path).
 * horizontal=false intercambia ejes u/v (Vertical warp VC).
 */
export function envelopeWarpMark(
  mark,
  { style = "arc", bend = 50, horizontal = true, h = 0, v = 0 } = {},
) {
  if (!mark || mark.type === "text" || mark.type === "textpath") return mark;
  const asPath = markAsPath(mark);
  if (!asPath?.points?.length) return mark;
  const b = markBounds(asPath);
  if (!b || b.w < 1e-8 || b.h < 1e-8) return mark;
  const bendAmt = Math.max(-100, Math.min(100, Number(bend) || 0)) / 100;
  const hDist = Math.max(-100, Math.min(100, Number(h) || 0)) / 100;
  const vDist = Math.max(-100, Math.min(100, Number(v) || 0)) / 100;
  const horiz = horizontal !== false;
  const points = asPath.points.map((p) => {
    const u0 = (Number(p.x) - b.x) / b.w;
    const v0 = (Number(p.y) - b.y) / b.h;
    const u = horiz ? u0 : v0;
    const vv = horiz ? v0 : u0;
    const { du, dv } = envelopeWarpOffset(style, u, vv, bendAmt, hDist, vDist);
    const nu = u + du;
    const nv = vv + dv;
    const x = horiz ? b.x + nu * b.w : b.x + nv * b.w;
    const y = horiz ? b.y + nv * b.h : b.y + nu * b.h;
    return { x, y };
  });
  return clampMark(
    {
      ...asPath,
      id: mark.id,
      type: "path",
      points,
      envelope: {
        style: String(style || "arc"),
        bend: Math.round(bendAmt * 100),
        horizontal: horiz,
        h: Math.round(hDist * 100),
        v: Math.round(vDist * 100),
      },
    },
    { allowOutside: true },
  );
}

/** Aplica Envelope Warp a ids seleccionados. */
export function envelopeWarpMarks(marks, selectedIds, opts = {}) {
  const list = Array.isArray(marks) ? marks : [];
  const ids = new Set((selectedIds || []).map(String));
  if (!ids.size) return list;
  let changed = false;
  const next = list.map((m) => {
    if (!ids.has(String(m.id))) return m;
    const warped = envelopeWarpMark(m, opts);
    if (warped !== m) changed = true;
    return warped;
  });
  return changed ? next : list;
}

/** Remuestrea polilínea a n puntos (longitud arc). */
function densifyPolyline(points, n, closed = false) {
  const pts = (Array.isArray(points) ? points : [])
    .map((p) => ({ x: Number(p.x) || 0, y: Number(p.y) || 0 }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  if (pts.length < 2 || n < 2) return pts;
  const segs = [];
  let total = 0;
  const count = closed && pts.length >= 3 ? pts.length : pts.length - 1;
  for (let i = 0; i < count; i += 1) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    segs.push({ a, b, len, acc: total });
    total += len;
  }
  if (total < 1e-10) return pts;
  const out = [];
  const samples = closed ? n : n;
  for (let i = 0; i < samples; i += 1) {
    const target = closed ? (i / samples) * total : (i / (samples - 1)) * total;
    let placed = false;
    for (let s = 0; s < segs.length; s += 1) {
      const seg = segs[s];
      if (target <= seg.acc + seg.len + 1e-12 || s === segs.length - 1) {
        const u = seg.len < 1e-12 ? 0 : (target - seg.acc) / seg.len;
        const uu = Math.max(0, Math.min(1, u));
        out.push({
          x: seg.a.x + (seg.b.x - seg.a.x) * uu,
          y: seg.a.y + (seg.b.y - seg.a.y) * uu,
        });
        placed = true;
        break;
      }
    }
    if (!placed) out.push({ ...pts[pts.length - 1] });
  }
  return out;
}

/**
 * Object › Envelope Distort › Make with Mesh (VC).
 * Densifica el path y guarda rejilla de control (identidad) rows×cols.
 */
export function envelopeMakeWithMesh(mark, { rows = 3, cols = 4 } = {}) {
  if (!mark || mark.type === "text" || mark.type === "textpath") return null;
  const asPath = markAsPath(mark);
  if (!asPath?.points?.length) return null;
  const r = Math.max(1, Math.min(12, Math.round(Number(rows) || 3)));
  const c = Math.max(1, Math.min(12, Math.round(Number(cols) || 4)));
  const closed = Boolean(asPath.closed ?? mark.closed);
  const targetPts = Math.max(24, (r + 1) * (c + 1) * 3);
  const points = densifyPolyline(asPath.points, targetPts, closed);
  const b = markBounds({ ...asPath, points });
  if (!b) return null;
  const meshPoints = [];
  for (let i = 0; i <= r; i += 1) {
    for (let j = 0; j <= c; j += 1) {
      const u = j / c;
      const v = i / r;
      meshPoints.push({
        u,
        v,
        x: b.x + b.w * u,
        y: b.y + b.h * v,
        rest: { x: b.x + b.w * u, y: b.y + b.h * v },
      });
    }
  }
  // Pines Puppet en cruces de malla (edición rápida tipo envelope)
  const pins = meshPoints
    .filter((_, idx) => {
      const i = Math.floor(idx / (c + 1));
      const j = idx % (c + 1);
      return (
        i % Math.max(1, Math.floor(r / 2)) === 0 ||
        j % Math.max(1, Math.floor(c / 2)) === 0
      );
    })
    .slice(0, 16)
    .map((p) => ({
      rest: { x: p.rest.x, y: p.rest.y },
      at: { x: p.x, y: p.y },
    }));
  return clampMark(
    {
      ...asPath,
      id: mark.id,
      type: "path",
      closed,
      points,
      envelope: {
        type: "mesh",
        rows: r,
        cols: c,
        points: meshPoints,
      },
      ...(pins.length >= 3
        ? { puppet: { restPoints: points.map((p) => ({ ...p })), pins } }
        : null),
    },
    { allowOutside: true },
  );
}

/** Aplica Make with Mesh a ids seleccionados. */
export function envelopeMeshMarks(
  marks,
  selectedIds,
  { rows = 3, cols = 4 } = {},
) {
  const list = Array.isArray(marks) ? marks : [];
  const ids = new Set((selectedIds || []).map(String));
  if (!ids.size) return list;
  let changed = false;
  const next = list.map((m) => {
    if (!ids.has(String(m.id))) return m;
    const meshed = envelopeMakeWithMesh(m, { rows, cols });
    if (!meshed) return m;
    changed = true;
    return meshed;
  });
  return changed ? next : list;
}

/**
 * Object › Objects on Path › Attach (VC).
 * Reparte `memberIds` a lo largo de `pathId` (orden lienzo L→R, T→B).
 * followPath: rota cada marca a la tangente + rotateExtraDeg.
 * Devuelve null si no aplica.
 */
export function attachMarksToPath(
  marks,
  pathId,
  memberIds,
  { rotateExtraDeg = 0, followPath = true } = {},
) {
  const list = Array.isArray(marks) ? marks : [];
  const pid = String(pathId || "");
  const pathMark = list.find((m) => String(m.id) === pid);
  const asPath = markAsPath(pathMark);
  if (!asPath?.points || asPath.points.length < 2) return null;
  const closed = Boolean(asPath.closed ?? pathMark?.closed);
  const rawIds = (memberIds || []).map(String).filter((id) => id && id !== pid);
  if (!rawIds.length) return null;
  const members = rawIds
    .map((id) => list.find((m) => String(m.id) === id))
    .filter(Boolean)
    .sort((a, b) => {
      const ba = markBounds(a);
      const bb = markBounds(b);
      if (!ba || !bb) return 0;
      if (Math.abs(ba.cx - bb.cx) > 0.02) return ba.cx - bb.cx;
      return ba.cy - bb.cy;
    });
  if (!members.length) return null;
  const n = members.length;
  const tilt = Number(rotateExtraDeg) || 0;
  const follow = followPath !== false;
  const placed = new Map();
  const memberMeta = [];
  members.forEach((m, i) => {
    const t = n === 1 ? (closed ? 0 : 0.5) : i / (n - 1);
    const s = samplePolylineAtT(asPath.points, t, closed);
    if (!s) return;
    const b = markBounds(m);
    if (!b) return;
    // Tangente: nx=-ty, ny=tx → (tx,ty)=(ny,-nx)
    const tangDeg = (Math.atan2(-s.nx, s.ny) * 180) / Math.PI;
    let next = translateMark(m, s.x - b.cx, s.y - b.cy, {
      allowOutside: true,
    });
    next = clampMark(
      {
        ...next,
        ...(follow ? { rotation: normalizeRotation(tangDeg + tilt) } : null),
        onPath: { pathId: pid, t },
      },
      { allowOutside: true },
    );
    placed.set(String(m.id), next);
    memberMeta.push({ id: String(m.id), t });
  });
  if (!placed.size) return null;
  const groupId = newGroupId();
  return list.map((m) => {
    if (String(m.id) === pid) {
      return clampMark(
        {
          ...m,
          groupId,
          objectsOnPath: {
            members: memberMeta,
            rotate: tilt,
            followPath: follow,
          },
        },
        { allowOutside: true },
      );
    }
    const p = placed.get(String(m.id));
    if (!p) return m;
    return clampMark({ ...p, groupId }, { allowOutside: true });
  });
}

/**
 * Object › Objects on Path › Expand: quita vínculo path↔objetos (deja posiciones).
 */
export function expandObjectsOnPath(marks, selectedIds) {
  const list = Array.isArray(marks) ? marks : [];
  const ids = new Set((selectedIds || []).map(String));
  if (!ids.size) return null;
  const pathIds = new Set();
  list.forEach((m) => {
    if (!ids.has(String(m.id))) return;
    if (m.objectsOnPath?.members?.length) pathIds.add(String(m.id));
    if (m.onPath?.pathId) pathIds.add(String(m.onPath.pathId));
  });
  if (!pathIds.size) return null;
  let changed = false;
  const next = list.map((m) => {
    const isPath = pathIds.has(String(m.id));
    const onLinked = m.onPath?.pathId && pathIds.has(String(m.onPath.pathId));
    if (!isPath && !onLinked) return m;
    changed = true;
    const { objectsOnPath: _o, onPath: _p, groupId: _g, ...rest } = m;
    return clampMark(rest, { allowOutside: true });
  });
  return changed ? next : null;
}

/**
 * Define un símbolo a partir de una marca (Symbols panel / Sprayer).
 * Devuelve la lista actualizada de símbolos + el nuevo entry.
 */
export function defineSymbolFromMark(symbols, mark, name = null) {
  const template = markAsSymbolTemplate(mark);
  if (!template) return null;
  const entry = normalizeSymbolEntry({
    id: newSymbolId(),
    name:
      name ||
      (mark.type === "text"
        ? String(mark.text || "Text").slice(0, 24)
        : mark.type || "Symbol"),
    mark: template,
  });
  if (!entry) return null;
  const list = Array.isArray(symbols) ? symbols.slice() : [];
  list.push(entry);
  return { symbols: list.slice(0, 48), symbol: entry };
}

/**
 * Instancia un símbolo con centro en (x,y).
 */
export function placeSymbolInstance(
  symbol,
  x,
  y,
  { rotationJitterDeg = 12, symbolId = null } = {},
) {
  const entry = normalizeSymbolEntry(symbol);
  if (!entry?.mark) return null;
  const sid = String(symbolId || entry.id);
  let m = clampMark(
    {
      ...JSON.parse(JSON.stringify(entry.mark)),
      id: newMarkId("sym"),
      symbolId: sid,
    },
    { allowOutside: true },
  );
  const b = markBounds(m);
  if (!b) return m;
  m = translateMark(m, (Number(x) || 0) - b.cx, (Number(y) || 0) - b.cy, {
    allowOutside: true,
  });
  const jitter = Number(rotationJitterDeg) || 0;
  if (jitter > 0) {
    const delta = (Math.random() * 2 - 1) * jitter;
    m = clampMark(
      {
        ...m,
        rotation: normalizeRotation((Number(m.rotation) || 0) + delta),
      },
      { allowOutside: true },
    );
  }
  return m;
}

/**
 * Symbol Sprayer (VC): añade instancias cerca de (cx,cy) o Alt = borra cercanas.
 * density 1–10 → nº de instancias por gesto; radius en coords 0–1.
 */
export function spraySymbolAt(
  marks,
  symbol,
  cx,
  cy,
  { radius = 0.05, density = 3, alt = false, rotationJitterDeg = 12 } = {},
) {
  const list = Array.isArray(marks) ? marks : [];
  const entry = normalizeSymbolEntry(symbol);
  if (!entry) return list;
  const r = Math.max(0.01, Number(radius) || 0.05);
  const px = Number(cx) || 0;
  const py = Number(cy) || 0;
  if (alt) {
    return list.filter((m) => {
      if (String(m.symbolId) !== String(entry.id)) return true;
      const b = markBounds(m);
      if (!b) return true;
      return Math.hypot(b.cx - px, b.cy - py) > r;
    });
  }
  const n = Math.max(1, Math.min(10, Math.round(Number(density) || 3)));
  const added = [];
  for (let i = 0; i < n; i += 1) {
    const ang = Math.random() * Math.PI * 2;
    const dist = n === 1 ? 0 : Math.random() * r;
    const inst = placeSymbolInstance(
      entry,
      px + Math.cos(ang) * dist,
      py + Math.sin(ang) * dist,
      { rotationJitterDeg },
    );
    if (inst) added.push(inst);
  }
  return added.length ? list.concat(added) : list;
}

/**
 * Symbol Shifter (VC): mueve instancias cerca de (cx,cy) según (dx,dy).
 * Alt = invertir dirección.
 */
export function shiftSymbolsNear(
  marks,
  symbolId,
  cx,
  cy,
  { radius = 0.1, dx = 0, dy = 0, alt = false } = {},
) {
  const list = Array.isArray(marks) ? marks : [];
  const sid = String(symbolId || "");
  if (!sid) return list;
  const r = Math.max(0.02, Number(radius) || 0.1);
  const ox = (Number(dx) || 0) * (alt ? -1 : 1);
  const oy = (Number(dy) || 0) * (alt ? -1 : 1);
  if (Math.hypot(ox, oy) < 1e-8) return list;
  const px = Number(cx) || 0;
  const py = Number(cy) || 0;
  return list.map((m) => {
    if (String(m.symbolId) !== sid) return m;
    const b = markBounds(m);
    if (!b) return m;
    const dist = Math.hypot(b.cx - px, b.cy - py);
    if (dist > r) return m;
    const t = 1 - dist / r;
    return translateMark(m, ox * t, oy * t, { allowOutside: true });
  });
}

/**
 * Symbol Scruncher: acerca (o Alt = aleja) instancias del símbolo hacia (cx,cy).
 */
export function scrunchSymbolsNear(
  marks,
  symbolId,
  cx,
  cy,
  { radius = 0.1, strength = 0.15, alt = false } = {},
) {
  const list = Array.isArray(marks) ? marks : [];
  const sid = String(symbolId || "");
  if (!sid) return list;
  const r = Math.max(0.02, Number(radius) || 0.1);
  const s = Math.max(0.02, Math.min(0.5, Number(strength) || 0.15));
  const px = Number(cx) || 0;
  const py = Number(cy) || 0;
  const sign = alt ? 1 : -1;
  return list.map((m) => {
    if (String(m.symbolId) !== sid) return m;
    const b = markBounds(m);
    if (!b) return m;
    const dx = b.cx - px;
    const dy = b.cy - py;
    const dist = Math.hypot(dx, dy);
    if (dist > r || dist < 1e-8) return m;
    const t = (1 - dist / r) * s * sign;
    return translateMark(m, dx * t, dy * t, { allowOutside: true });
  });
}

/** Escala una marca alrededor de su centro (factor > 0). */
function scaleMarkAboutCenter(mark, factor) {
  const f = Number(factor);
  if (!mark || !Number.isFinite(f) || f <= 0 || Math.abs(f - 1) < 1e-6) {
    return mark;
  }
  const b = markBounds(mark);
  if (!b) return mark;
  const bbox1 = {
    x: b.cx - (b.w * f) / 2,
    y: b.cy - (b.h * f) / 2,
    w: Math.max(0.004, b.w * f),
    h: Math.max(0.004, b.h * f),
  };
  const next = scaleMarksWithBBox([mark], b, bbox1)[0];
  return clampMark(
    { ...next, symbolId: mark.symbolId },
    { allowOutside: true },
  );
}

/**
 * Symbol Sizer (VC): agranda (Alt = reduce) instancias cerca de (cx,cy).
 */
export function sizeSymbolsNear(
  marks,
  symbolId,
  cx,
  cy,
  { radius = 0.1, strength = 0.12, alt = false } = {},
) {
  const list = Array.isArray(marks) ? marks : [];
  const sid = String(symbolId || "");
  if (!sid) return list;
  const r = Math.max(0.02, Number(radius) || 0.1);
  const s = Math.max(0.02, Math.min(0.4, Number(strength) || 0.12));
  const px = Number(cx) || 0;
  const py = Number(cy) || 0;
  return list.map((m) => {
    if (String(m.symbolId) !== sid) return m;
    const b = markBounds(m);
    if (!b) return m;
    const dist = Math.hypot(b.cx - px, b.cy - py);
    if (dist > r) return m;
    const t = 1 - dist / r;
    const factor = alt ? 1 - s * t : 1 + s * t;
    return scaleMarkAboutCenter(m, Math.max(0.15, Math.min(4, factor)));
  });
}

/**
 * Symbol Spinner (VC): rota instancias cerca de (cx,cy) hacia la dirección del gesto
 * (usa ángulo del vector desde el centro de la marca al cursor).
 * Alt = sentido contrario.
 */
export function spinSymbolsNear(
  marks,
  symbolId,
  cx,
  cy,
  { radius = 0.12, strengthDeg = 18, alt = false } = {},
) {
  const list = Array.isArray(marks) ? marks : [];
  const sid = String(symbolId || "");
  if (!sid) return list;
  const r = Math.max(0.02, Number(radius) || 0.12);
  const s = Math.max(1, Math.min(45, Number(strengthDeg) || 18));
  const px = Number(cx) || 0;
  const py = Number(cy) || 0;
  const sign = alt ? -1 : 1;
  return list.map((m) => {
    if (String(m.symbolId) !== sid) return m;
    const b = markBounds(m);
    if (!b) return m;
    const dist = Math.hypot(b.cx - px, b.cy - py);
    if (dist > r) return m;
    const t = 1 - dist / r;
    const delta = s * t * sign;
    // También gira la geometría (paths) alrededor del centro
    const rotated = rotateMarksAroundBBox([m], b, delta)[0];
    return clampMark(
      {
        ...rotated,
        symbolId: m.symbolId,
        rotation: normalizeRotation((Number(m.rotation) || 0) + delta),
      },
      { allowOutside: true },
    );
  });
}

/**
 * Symbol Stainer (VC): mezcla fill/stroke hacia `tint` (Alt = hacia blanco / destiñe).
 */
export function stainSymbolsNear(
  marks,
  symbolId,
  cx,
  cy,
  { radius = 0.1, strength = 0.25, tint = "#d9472f", alt = false } = {},
) {
  const list = Array.isArray(marks) ? marks : [];
  const sid = String(symbolId || "");
  if (!sid) return list;
  const r = Math.max(0.02, Number(radius) || 0.1);
  const s = Math.max(0.05, Math.min(0.8, Number(strength) || 0.25));
  const target = alt ? "#ffffff" : tint || "#d9472f";
  const px = Number(cx) || 0;
  const py = Number(cy) || 0;
  return list.map((m) => {
    if (String(m.symbolId) !== sid) return m;
    const b = markBounds(m);
    if (!b) return m;
    const dist = Math.hypot(b.cx - px, b.cy - py);
    if (dist > r) return m;
    const t = (1 - dist / r) * s;
    const paint = markPaintColors(m);
    const nextFill =
      paint.fill && paint.fill !== "none"
        ? lerpHexColor(paint.fill, target, t)
        : paint.fill;
    const nextStroke = lerpHexColor(
      paint.strokeColor || m.color || "#111111",
      target,
      t,
    );
    return clampMark(
      {
        ...m,
        fill: nextFill != null ? nextFill : m.fill,
        color: nextStroke,
        strokeColor: nextStroke,
        symbolId: m.symbolId,
      },
      { allowOutside: true },
    );
  });
}

/**
 * Symbol Screener (VC): baja opacidad (Alt = sube) cerca de (cx,cy).
 */
export function screenSymbolsNear(
  marks,
  symbolId,
  cx,
  cy,
  { radius = 0.1, strength = 0.12, alt = false } = {},
) {
  const list = Array.isArray(marks) ? marks : [];
  const sid = String(symbolId || "");
  if (!sid) return list;
  const r = Math.max(0.02, Number(radius) || 0.1);
  const s = Math.max(0.02, Math.min(0.35, Number(strength) || 0.12));
  const px = Number(cx) || 0;
  const py = Number(cy) || 0;
  return list.map((m) => {
    if (String(m.symbolId) !== sid) return m;
    const b = markBounds(m);
    if (!b) return m;
    const dist = Math.hypot(b.cx - px, b.cy - py);
    if (dist > r) return m;
    const t = (1 - dist / r) * s;
    const cur = Number.isFinite(Number(m.opacity)) ? Number(m.opacity) : 1;
    const next = alt ? Math.min(1, cur + t) : Math.max(0.08, cur - t);
    return clampMark(
      { ...m, opacity: next, symbolId: m.symbolId },
      { allowOutside: true },
    );
  });
}

/**
 * Inferir path + miembros desde selección (último path / path con más puntos).
 */
export function resolveObjectsOnPathTargets(marks, selectedIds) {
  const list = Array.isArray(marks) ? marks : [];
  const ids = (selectedIds || []).map(String);
  if (ids.length < 2) return null;
  const selected = ids
    .map((id) => list.find((m) => String(m.id) === id))
    .filter(Boolean);
  const isNativePath = (m) =>
    m.type === "path" ||
    m.type === "line" ||
    m.type === "arrow" ||
    m.type === "arc" ||
    m.type === "spiral";
  const paths = selected.filter((m) => {
    const p = markAsPath(m);
    return p?.points?.length >= 2;
  });
  if (!paths.length) return null;
  // Preferir path/línea nativos (no rect→path), luego abiertos, luego más puntos
  paths.sort((a, b) => {
    const na = isNativePath(a) ? 0 : 1;
    const nb = isNativePath(b) ? 0 : 1;
    if (na !== nb) return na - nb;
    const openA = a.type === "path" && !a.closed ? 0 : 1;
    const openB = b.type === "path" && !b.closed ? 0 : 1;
    if (openA !== openB) return openA - openB;
    const la = markAsPath(a)?.points?.length || 0;
    const lb = markAsPath(b)?.points?.length || 0;
    return lb - la;
  });
  const path = paths[0];
  const members = selected
    .filter((m) => String(m.id) !== String(path.id))
    .map((m) => String(m.id));
  if (!members.length) return null;
  return { pathId: String(path.id), memberIds: members };
}

/**
 * Distorsiona puntos cerca de (cx,cy). Afecta paths y formas (→ path).
 * mode: twirl | bloat | pucker | warp | reshape | scallop | crystallize | wrinkle
 */
export function distortMarksNear(
  marks,
  cx,
  cy,
  { mode = "twirl", radius = 0.08, strength = 0.35, angleDeg = 25 } = {},
) {
  const list = Array.isArray(marks) ? marks : [];
  const r = Math.max(0.01, Number(radius) || 0.08);
  const s = Math.min(1.5, Math.max(0.05, Number(strength) || 0.35));
  const ang = ((Number(angleDeg) || 25) * Math.PI) / 180;
  return list.map((m) => {
    const asPath = markAsPath(m);
    if (!asPath) return m;
    let changed = false;
    const points = asPath.points.map((p, i) => {
      const x = Number(p.x) || 0;
      const y = Number(p.y) || 0;
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.hypot(dx, dy);
      if (dist > r || dist < 1e-8) return p;
      const t = 1 - dist / r;
      const w = t * t * s;
      let nx = x;
      let ny = y;
      if (mode === "twirl") {
        const a = ang * w;
        const c = Math.cos(a);
        const sn = Math.sin(a);
        nx = cx + dx * c - dy * sn;
        ny = cy + dx * sn + dy * c;
      } else if (mode === "bloat") {
        const f = 1 + 0.45 * w;
        nx = cx + dx * f;
        ny = cy + dy * f;
      } else if (mode === "pucker") {
        const f = 1 - 0.45 * w;
        nx = cx + dx * f;
        ny = cy + dy * f;
      } else if (mode === "scallop") {
        const theta = Math.atan2(dy, dx);
        const wave = 1 + 0.35 * w * Math.sin(theta * 6);
        nx = cx + dx * wave;
        ny = cy + dy * wave;
      } else if (mode === "crystallize") {
        const theta = Math.atan2(dy, dx);
        const step = Math.PI / 4;
        const snapped = Math.round(theta / step) * step;
        const mix = 0.55 * w;
        const tx = Math.cos(snapped) * dist;
        const ty = Math.sin(snapped) * dist;
        nx = cx + dx * (1 - mix) + tx * mix;
        ny = cy + dy * (1 - mix) + ty * mix;
      } else if (mode === "wrinkle") {
        const ux = -dy / dist;
        const uy = dx / dist;
        const n = Math.sin((i + 1) * 2.7 + dist * 40) * 0.018 * w;
        nx = x + ux * n;
        ny = y + uy * n;
      } else if (mode === "reshape") {
        // Reformar: tira el trazo hacia el cursor (más fuerte que warp)
        nx = x + (cx - x) * 0.55 * w;
        ny = y + (cy - y) * 0.55 * w;
      } else {
        nx = x + (cx - x) * 0.25 * w;
        ny = y + (cy - y) * 0.25 * w;
      }
      changed = true;
      // Sin clamp01: liquify puede sacar vértices fuera de mesa (como Puppet).
      return { x: nx, y: ny };
    });
    if (!changed) return m;
    return clampMark(
      {
        ...asPath,
        id: m.id,
        points,
        type: "path",
      },
      { allowOutside: true },
    );
  });
}

const WIDTH_POINT_SAME_T = 0.035;

/**
 * Width tool (VC Shift+W):
 * - Clic cerca del trazo → punto de perfil {t, scale} (anchura variable).
 * - Clic en interior de forma (lejos del borde) → stroke uniforme ± delta.
 */
export function widthMarksAt(marks, x, y, delta = 0.5, tol = 0.05) {
  const list = Array.isArray(marks) ? marks : [];
  const d = Number(delta) || 0.5;
  const px = Number(x) || 0;
  const py = Number(y) || 0;
  const t = Math.max(0.01, Number(tol) || 0.05);
  let bestStroke = null;
  let bestFill = null;
  list.forEach((m) => {
    if (!m || m.type === "text" || m.type === "textpath") return;
    const asPath = markAsPath(m);
    const closed = Boolean(asPath?.closed ?? m.closed);
    const pts = asPath?.points;
    if (pts && pts.length >= 2) {
      const hit = nearestPolylineFraction(pts, px, py, closed);
      const reach = markStrokeSvg(m.stroke) * 0.55 + 0.02;
      if (hit && hit.dist <= Math.max(t, reach)) {
        if (!bestStroke || hit.dist < bestStroke.dist) {
          bestStroke = {
            id: String(m.id),
            dist: hit.dist,
            hit,
            asPath,
            mark: m,
          };
        }
        return;
      }
    }
    const b = markBounds(m);
    if (!b) return;
    const inside =
      px >= b.x - t &&
      px <= b.x + b.w + t &&
      py >= b.y - t &&
      py <= b.y + b.h + t;
    if (inside) {
      const dist = Math.hypot(px - b.cx, py - b.cy);
      if (!bestFill || dist < bestFill.dist) {
        bestFill = { id: String(m.id), dist };
      }
    }
  });
  if (bestStroke) {
    return list.map((m) => {
      if (String(m.id) !== bestStroke.id) return m;
      const pathMark =
        m.type === "path"
          ? m
          : {
              ...bestStroke.asPath,
              id: m.id,
              stroke: m.stroke,
              strokeColor: m.strokeColor || m.color,
              color: m.color,
              fill: m.fill != null ? m.fill : "none",
            };
      let wps = normalizeWidthPoints(pathMark.widthPoints);
      if (!wps.length)
        wps = [
          { t: 0, scale: 1 },
          { t: 1, scale: 1 },
        ];
      const frac = bestStroke.hit.t;
      const dScale = d * 0.22;
      const idx = wps.findIndex(
        (p) => Math.abs(p.t - frac) <= WIDTH_POINT_SAME_T,
      );
      if (idx >= 0) {
        wps = wps.map((p, i) =>
          i === idx
            ? {
                ...p,
                scale: Math.max(0.05, Math.min(8, p.scale + dScale)),
              }
            : p,
        );
      } else {
        const cur = widthScaleAtT(wps, frac);
        wps = normalizeWidthPoints([
          ...wps,
          { t: frac, scale: Math.max(0.05, Math.min(8, cur + dScale)) },
        ]);
      }
      return clampMark(
        {
          ...pathMark,
          type: "path",
          widthPoints: wps,
        },
        { allowOutside: true },
      );
    });
  }
  if (!bestFill) return list;
  return list.map((m) => {
    if (String(m.id) !== bestFill.id) return m;
    const stroke = Math.min(12, Math.max(0.5, (Number(m.stroke) || 2) + d));
    return clampMark({ ...m, stroke });
  });
}

function resamplePolyline(points, n) {
  const pts = (points || [])
    .map((p) => ({ x: Number(p.x) || 0, y: Number(p.y) || 0 }))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  if (pts.length < 2 || n < 2) return pts;
  const segLens = [];
  let total = 0;
  for (let i = 1; i < pts.length; i += 1) {
    const len = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
    segLens.push(len);
    total += len;
  }
  if (total < 1e-8) {
    return Array.from({ length: n }, () => ({ ...pts[0] }));
  }
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const target = (i / (n - 1)) * total;
    let acc = 0;
    let placed = false;
    for (let s = 0; s < segLens.length; s += 1) {
      if (acc + segLens[s] >= target || s === segLens.length - 1) {
        const local = segLens[s] > 1e-8 ? (target - acc) / segLens[s] : 0;
        const a = pts[s];
        const b = pts[s + 1];
        out.push({
          x: a.x + (b.x - a.x) * local,
          y: a.y + (b.y - a.y) * local,
        });
        placed = true;
        break;
      }
      acc += segLens[s];
    }
    if (!placed) out.push({ ...pts[pts.length - 1] });
  }
  return out;
}

/** Paint interpolado A→B en t∈[0,1] (fill / stroke / color). */
function blendPaintAt(a, b, t) {
  const pa = markPaintColors(a);
  const pb = markPaintColors(b);
  const fillA = pa.fill && pa.fill !== "none" ? pa.fill : null;
  const fillB = pb.fill && pb.fill !== "none" ? pb.fill : null;
  let fill = "none";
  if (fillA && fillB) fill = lerpHexColor(fillA, fillB, t);
  else if (fillA) fill = fillA;
  else if (fillB) fill = fillB;
  const strokeColor = lerpHexColor(
    pa.strokeColor || pa.ink,
    pb.strokeColor || pb.ink,
    t,
  );
  const color = lerpHexColor(pa.ink, pb.ink, t);
  const stroke =
    (Number(a.stroke) || 2) * (1 - t) + (Number(b.stroke) || 2) * t;
  return { fill, strokeColor, color, stroke };
}

/**
 * Blend entre 2 marcas → N pasos (mantiene originales).
 * Paths: morph por resample; rect/ellipse mismo tipo: lerp bbox.
 * Colores: lerp fill/stroke (antes solo copiaba el de A).
 */
export function blendTwoMarks(marks, idA, idB, steps = 4) {
  const list = Array.isArray(marks) ? marks : [];
  const a = list.find((m) => String(m.id) === String(idA));
  const b = list.find((m) => String(m.id) === String(idB));
  if (!a || !b) return null;
  const n = Math.max(1, Math.min(20, Math.round(Number(steps) || 4)));
  const pa = markAsPath(a);
  const pb = markAsPath(b);
  const mid = [];
  const z = Math.max(Number(a.z) || 0, Number(b.z) || 0);

  if (pa && pb && pa.points.length >= 2 && pb.points.length >= 2) {
    const count = Math.max(
      12,
      Math.min(64, Math.max(pa.points.length, pb.points.length) * 2),
    );
    const sa = resamplePolyline(pa.points, count);
    const sb = resamplePolyline(pb.points, count);
    for (let i = 1; i <= n; i += 1) {
      const t = i / (n + 1);
      const paint = blendPaintAt(a, b, t);
      mid.push(
        clampMark({
          id: newMarkId(),
          type: "path",
          closed: Boolean(pa.closed || pb.closed || a.closed || b.closed),
          points: sa.map((p, j) => ({
            x: p.x + (sb[j].x - p.x) * t,
            y: p.y + (sb[j].y - p.y) * t,
          })),
          ...paint,
          z,
        }),
      );
    }
    return list.concat(mid);
  }

  const ba = markBounds(a);
  const bb = markBounds(b);
  if (!ba || !bb) return null;
  for (let i = 1; i <= n; i += 1) {
    const t = i / (n + 1);
    const type =
      a.type === b.type && (a.type === "rect" || a.type === "ellipse")
        ? a.type
        : "ellipse";
    const paint = blendPaintAt(a, b, t);
    mid.push(
      clampMark({
        id: newMarkId(),
        type,
        x: ba.x + (bb.x - ba.x) * t,
        y: ba.y + (bb.y - ba.y) * t,
        w: ba.w + (bb.w - ba.w) * t,
        h: ba.h + (bb.h - ba.h) * t,
        ...paint,
        z,
      }),
    );
  }
  return list.concat(mid);
}

function pointInRect(px, py, rect) {
  return (
    px >= rect.x &&
    px <= rect.x + rect.w &&
    py >= rect.y &&
    py <= rect.y + rect.h
  );
}

/**
 * Rect cut: recorta paths/formas contra el rect.
 * mode: "delete-inside" | "keep-inside"
 */
export function rectCutMarks(marks, rect, mode = "delete-inside") {
  const list = Array.isArray(marks) ? marks : [];
  const rx = Number(rect?.x) || 0;
  const ry = Number(rect?.y) || 0;
  const rw = Math.max(0.001, Number(rect?.w) || 0);
  const rh = Math.max(0.001, Number(rect?.h) || 0);
  const box = { x: rx, y: ry, w: rw, h: rh };
  const keepInside = mode === "keep-inside";
  const out = [];

  list.forEach((m) => {
    const asPath = markAsPath(m);
    if (asPath && asPath.points.length >= 2) {
      const kept = asPath.points.filter((p) => {
        const hit = pointInRect(Number(p.x) || 0, Number(p.y) || 0, box);
        return keepInside ? hit : !hit;
      });
      if (kept.length < 2) return;
      // Si casi no cambió, y era geom → dejar original
      if (
        kept.length === asPath.points.length &&
        m.type !== "path" &&
        !keepInside
      ) {
        const b = markBounds(m);
        if (b && !pointInRect(b.cx, b.cy, box)) {
          out.push(m);
          return;
        }
      }
      out.push(
        clampMark({
          ...asPath,
          id: m.id,
          type: "path",
          points: kept,
        }),
      );
      return;
    }
    const b = markBounds(m);
    if (!b) {
      out.push(m);
      return;
    }
    const hit = pointInRect(b.cx, b.cy, box);
    if (keepInside ? hit : !hit) out.push(m);
  });
  return out;
}

/**
 * Mirror cut: knife en el eje + refleja la mitad cortada.
 */
export function mirrorCutMarks(marks, x, y, axis = "v") {
  const list = Array.isArray(marks) ? marks : [];
  const ax = Number(x) || 0;
  const ay = Number(y) || 0;
  const knife =
    axis === "v"
      ? [
          { x: ax, y: 0 },
          { x: ax, y: 1 },
        ]
      : [
          { x: 0, y: ay },
          { x: 1, y: ay },
        ];
  const split = knifeCutMarks(list, knife, 0.04);
  const out = [];
  const seen = new Set();
  split.forEach((m) => {
    const b = markBounds(m);
    if (!b) {
      out.push(m);
      return;
    }
    const onKeepSide = axis === "v" ? b.cx <= ax + 0.001 : b.cy <= ay + 0.001;
    if (onKeepSide) {
      out.push(m);
      return;
    }
    const mirrored = reflectMarkAcross(m, axis === "v" ? ax : ay, axis);
    if (mirrored && !seen.has(String(mirrored.id))) {
      seen.add(String(mirrored.id));
      out.push(mirrored);
    }
  });
  return out;
}

/** Paint visible para resultado Pathfinder (nunca stroke-only invisible). */
function pathfinderPaintFrom(mark) {
  const { fill, strokeColor, ink } = markPaintColors(mark || {});
  const visibleFill = fill && fill !== "none" ? fill : ink;
  return {
    color: ink,
    strokeColor,
    stroke: Math.max(0.5, Number(mark?.stroke) || 2),
    fill: visibleFill,
  };
}

/** Contorno cerrado → ring GeoJSON [x,y][] (primer=último). */
function markToRing(mark) {
  const outline = markOutlinePoints(mark, 64);
  if (!outline || outline.length < 3) return null;
  const ring = outline.map((p) => [Number(p.x) || 0, Number(p.y) || 0]);
  const a = ring[0];
  const b = ring[ring.length - 1];
  if (Math.hypot(a[0] - b[0], a[1] - b[1]) > 1e-6) {
    ring.push([a[0], a[1]]);
  }
  if (ring.length < 4) return null;
  return ring;
}

function markToPoly(mark) {
  const ring = markToRing(mark);
  return ring ? [ring] : null;
}

function ringToPoints(ring) {
  return (ring || []).map(([x, y]) => ({
    x: Number(x) || 0,
    y: Number(y) || 0,
  }));
}

/** MultiPolygon → marcas path cerradas (holes → evenodd). */
function polysToPathMarks(multiPoly, paint) {
  const out = [];
  const polys = Array.isArray(multiPoly) ? multiPoly : [];
  polys.forEach((poly) => {
    const rings = Array.isArray(poly) ? poly : [];
    if (!rings.length || !rings[0] || rings[0].length < 4) return;
    const points = ringToPoints(rings[0]);
    if (points.length < 3) return;
    const holes = rings
      .slice(1)
      .map(ringToPoints)
      .filter((h) => h.length >= 3);
    out.push(
      clampMark({
        id: newMarkId(),
        type: "path",
        closed: true,
        points,
        ...(holes.length ? { holes, fillRule: "evenodd" } : null),
        ...paint,
      }),
    );
  });
  return out;
}

function runPolygonOp(mode, polyA, polyB) {
  try {
    if (mode === "unite" || mode === "union") {
      return polygonClipping.union(polyA, polyB);
    }
    if (mode === "minus" || mode === "subtract" || mode === "minusfront") {
      return polygonClipping.difference(polyA, polyB);
    }
    if (mode === "intersect" || mode === "intersection") {
      return polygonClipping.intersection(polyA, polyB);
    }
    if (mode === "exclude" || mode === "xor") {
      return polygonClipping.xor(polyA, polyB);
    }
  } catch {
    return null;
  }
  return null;
}

function isAxisRectMark(m) {
  const t = String(m?.type || "").toLowerCase();
  return t === "rect" || t === "roundrect";
}

/** Rects AABB: a − b (hasta 4 piezas). */
function aabbSubtract(a, b) {
  const ix1 = Math.max(a.x, b.x);
  const iy1 = Math.max(a.y, b.y);
  const ix2 = Math.min(a.x + a.w, b.x + b.w);
  const iy2 = Math.min(a.y + a.h, b.y + b.h);
  if (ix2 - ix1 < 0.005 || iy2 - iy1 < 0.005) {
    return [{ ...a }];
  }
  const parts = [];
  if (iy1 > a.y + 0.004) {
    parts.push({ x: a.x, y: a.y, w: a.w, h: iy1 - a.y });
  }
  if (a.y + a.h > iy2 + 0.004) {
    parts.push({ x: a.x, y: iy2, w: a.w, h: a.y + a.h - iy2 });
  }
  if (ix1 > a.x + 0.004) {
    parts.push({ x: a.x, y: iy1, w: ix1 - a.x, h: iy2 - iy1 });
  }
  if (a.x + a.w > ix2 + 0.004) {
    parts.push({ x: ix2, y: iy1, w: a.x + a.w - ix2, h: iy2 - iy1 });
  }
  return parts.filter((p) => p.w > 0.008 && p.h > 0.008);
}

function makePaintedRect(box, paint, type = "rect", extra = {}) {
  return clampMark({
    id: newMarkId(),
    type,
    x: box.x,
    y: box.y,
    w: box.w,
    h: box.h,
    ...paint,
    ...extra,
  });
}

/** Si el path cerrado es un rectángulo AABB sólido → {x,y,w,h}; si no, null. */
function tryPathAsAxisRect(mark) {
  const pts = mark?.points || [];
  if (!mark?.closed || pts.length < 4) return null;
  const xs = pts.map((p) => Number(p.x) || 0);
  const ys = pts.map((p) => Number(p.y) || 0);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const w = maxX - minX;
  const h = maxY - minY;
  if (w < 0.008 || h < 0.008) return null;
  const eps = 0.003;
  const onBoundary = pts.every((p) => {
    const x = Number(p.x) || 0;
    const y = Number(p.y) || 0;
    const onV = Math.abs(x - minX) <= eps || Math.abs(x - maxX) <= eps;
    const onH = Math.abs(y - minY) <= eps || Math.abs(y - maxY) <= eps;
    return onV || onH;
  });
  if (!onBoundary) return null;
  // Área del polígono ≈ AABB → no es L ni anillo
  let area = 0;
  for (let i = 0; i < pts.length; i += 1) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    area += (Number(a.x) || 0) * (Number(b.y) || 0);
    area -= (Number(b.x) || 0) * (Number(a.y) || 0);
  }
  area = Math.abs(area) * 0.5;
  if (area < w * h * 0.9) return null;
  return { x: minX, y: minY, w, h };
}

/**
 * Pathfinder — Unite / Minus / Intersect / Exclude.
 * ≥2 formas: pliega en orden de selección.
 * Rects AABB (unite/intersect/minus N; exclude solo 2). Resto: polygon-clipping.
 * Minus: A − B − C… · null = no tocar originales.
 */
export function pathfinderMarks(marks, selectedIds, op = "unite") {
  const ids = (selectedIds || []).map(String);
  if (ids.length < 2) return null;
  const list = Array.isArray(marks) ? marks : [];
  const selected = ids
    .map((id) => list.find((m) => String(m.id) === id))
    .filter(Boolean);
  if (selected.length < 2) return null;

  const mode = String(op || "unite").toLowerCase();
  const paint = pathfinderPaintFrom(selected[0]);
  const drop = new Set(selected.map((m) => String(m.id)));
  const rest = list.filter((m) => !drop.has(String(m.id)));

  const allAxisRect = selected.every(isAxisRectMark);
  const outType =
    allAxisRect && selected.every((m) => m.type === "roundrect")
      ? "roundrect"
      : "rect";
  const rxExtra =
    outType === "roundrect"
      ? {
          rx: Math.min(...selected.map((m) => Number(m.rx) || 0.04)),
        }
      : {};

  if (allAxisRect) {
    const boxes = selected.map((m) => markBounds(m)).filter(Boolean);
    if (boxes.length !== selected.length) return null;

    // Unite: no usar AABB (rellenaba huecos entre rects). Polygon-clipping
    // + promover a rect si el resultado es un rectángulo sólido.
    if (mode === "unite" || mode === "union") {
      let accU = markToPoly(selected[0]);
      if (!accU) return null;
      for (let i = 1; i < selected.length; i += 1) {
        const polyB = markToPoly(selected[i]);
        if (!polyB) return null;
        const result = runPolygonOp("unite", accU, polyB);
        if (!result || !result.length) return null;
        accU = result;
      }
      const createdU = polysToPathMarks(accU, paint);
      if (!createdU.length) return null;
      const promoted = createdU.map((m) => {
        const box = tryPathAsAxisRect(m);
        if (!box) return m;
        return makePaintedRect(box, paint, outType, rxExtra);
      });
      return rest.concat(promoted);
    }
    if (mode === "intersect" || mode === "intersection") {
      let x1 = boxes[0].x;
      let y1 = boxes[0].y;
      let x2 = boxes[0].x + boxes[0].w;
      let y2 = boxes[0].y + boxes[0].h;
      for (let i = 1; i < boxes.length; i += 1) {
        x1 = Math.max(x1, boxes[i].x);
        y1 = Math.max(y1, boxes[i].y);
        x2 = Math.min(x2, boxes[i].x + boxes[i].w);
        y2 = Math.min(y2, boxes[i].y + boxes[i].h);
      }
      if (x2 - x1 < 0.008 || y2 - y1 < 0.008) return null;
      return rest.concat([
        makePaintedRect(
          { x: x1, y: y1, w: x2 - x1, h: y2 - y1 },
          paint,
          outType,
          rxExtra,
        ),
      ]);
    }
    if (mode === "minus" || mode === "subtract" || mode === "minusfront") {
      let pieces = [{ ...boxes[0] }];
      for (let i = 1; i < boxes.length; i += 1) {
        pieces = pieces.flatMap((p) => aabbSubtract(p, boxes[i]));
      }
      if (!pieces.length) return null;
      return rest.concat(
        pieces.map((p) => makePaintedRect(p, paint, outType, rxExtra)),
      );
    }
    if ((mode === "exclude" || mode === "xor") && selected.length === 2) {
      const paintB = pathfinderPaintFrom(selected[1]);
      const fromA = aabbSubtract(boxes[0], boxes[1]).map((p) =>
        makePaintedRect(p, paint, outType, rxExtra),
      );
      const fromB = aabbSubtract(boxes[1], boxes[0]).map((p) =>
        makePaintedRect(p, paintB, outType, rxExtra),
      );
      if (!fromA.length && !fromB.length) return null;
      return rest.concat(fromA, fromB);
    }
  }

  let acc = markToPoly(selected[0]);
  if (!acc) return null;
  for (let i = 1; i < selected.length; i += 1) {
    const polyB = markToPoly(selected[i]);
    if (!polyB) return null;
    const result = runPolygonOp(mode, acc, polyB);
    if (!result || !result.length) return null;
    acc = result;
  }

  const created = polysToPathMarks(acc, paint);
  if (!created.length) return null;
  return rest.concat(created);
}

function reflectMarkAcross(mark, axisPos, axis) {
  const b = markBounds(mark);
  if (!b) return null;
  if (mark.type === "path" && Array.isArray(mark.points)) {
    const points = mark.points.map((p) => {
      if (axis === "v") {
        return {
          x: clamp01(2 * axisPos - (Number(p.x) || 0)),
          y: Number(p.y) || 0,
        };
      }
      return {
        x: Number(p.x) || 0,
        y: clamp01(2 * axisPos - (Number(p.y) || 0)),
      };
    });
    return clampMark({ ...mark, id: newMarkId(), points });
  }
  if (
    mark.type === "rect" ||
    mark.type === "ellipse" ||
    mark.type === "roundrect" ||
    mark.type === "polygon" ||
    mark.type === "star"
  ) {
    if (axis === "v") {
      const cx = b.cx;
      const ncx = 2 * axisPos - cx;
      return clampMark({
        ...mark,
        id: newMarkId(),
        x: ncx - b.w / 2,
        y: mark.y,
        w: mark.w,
        h: mark.h,
      });
    }
    const cy = b.cy;
    const ncy = 2 * axisPos - cy;
    return clampMark({
      ...mark,
      id: newMarkId(),
      x: mark.x,
      y: ncy - b.h / 2,
      w: mark.w,
      h: mark.h,
    });
  }
  if (mark.type === "line" || mark.type === "arrow") {
    if (axis === "v") {
      return clampMark({
        ...mark,
        id: newMarkId(),
        x1: clamp01(2 * axisPos - (Number(mark.x1) || 0)),
        x2: clamp01(2 * axisPos - (Number(mark.x2) || 0)),
        y1: mark.y1,
        y2: mark.y2,
      });
    }
    return clampMark({
      ...mark,
      id: newMarkId(),
      x1: mark.x1,
      x2: mark.x2,
      y1: clamp01(2 * axisPos - (Number(mark.y1) || 0)),
      y2: clamp01(2 * axisPos - (Number(mark.y2) || 0)),
    });
  }
  return null;
}

/**
 * Clip en space:"local" viaja pegado al marco (no hay que remapear).
 * Legacy (coords de página): remapea y migra a local.
 * Si el usuario editó la geometría del clip, respeta next.clip.
 */
export function syncSlotClipWithFrame(prev, next) {
  if (!next) return next;
  // Clip quitado a propósito
  if (prev?.clip && !next.clip) return next;
  if (
    prev?.clip &&
    next?.clip &&
    slotClipGeomKey(prev.clip) !== slotClipGeomKey(next.clip)
  ) {
    // Edición de máscara: conservar geometría nueva + pintura `from`
    const from = next.clip.from || prev.clip.from;
    return {
      ...next,
      clip: normalizeSlotClip(from ? { ...next.clip, from } : next.clip),
    };
  }
  if (prev?.clip?.space === "local") {
    const from = prev.clip.from || next?.clip?.from;
    return {
      ...next,
      clip: normalizeSlotClip(from ? { ...prev.clip, from } : prev.clip),
    };
  }
  if (next?.clip?.space === "local") {
    return { ...next, clip: normalizeSlotClip(next.clip) };
  }
  if (!prev?.clip) {
    const ensured = ensureLocalClip(next.clip, next);
    return ensured ? { ...next, clip: ensured } : next;
  }
  if (!next.clip) return next;
  const clip = prev.clip;
  const px = Number(prev.x) || 0;
  const py = Number(prev.y) || 0;
  const pw = Math.max(Number(prev.w) || 0.001, 0.001);
  const ph = Math.max(Number(prev.h) || 0.001, 0.001);
  const nx = Number(next.x) || 0;
  const ny = Number(next.y) || 0;
  const nw = Math.max(Number(next.w) || 0.001, 0.001);
  const nh = Math.max(Number(next.h) || 0.001, 0.001);
  const sx = nw / pw;
  const sy = nh / ph;
  const mapPt = (x, y) => ({
    x: nx + ((Number(x) || 0) - px) * sx,
    y: ny + ((Number(y) || 0) - py) * sy,
  });
  let mapped;
  if (clip.type === "path" && Array.isArray(clip.points)) {
    mapped = {
      ...clip,
      points: clip.points.map((p) => mapPt(p.x, p.y)),
    };
  } else if (clip.x != null) {
    const o = mapPt(clip.x, clip.y);
    mapped = {
      ...clip,
      x: o.x,
      y: o.y,
      w: (Number(clip.w) || 0) * sx,
      h: (Number(clip.h) || 0) * sy,
    };
  } else {
    return next;
  }
  const local = pageClipToLocal(mapped, next);
  if (!local) {
    const { clip: _c, ...rest } = next;
    return rest;
  }
  return { ...next, clip: local };
}

function rectsOverlapArea(a, b) {
  if (!a || !b) return 0;
  const x1 = Math.max(Number(a.x) || 0, Number(b.x) || 0);
  const y1 = Math.max(Number(a.y) || 0, Number(b.y) || 0);
  const x2 = Math.min(
    (Number(a.x) || 0) + (Number(a.w) || 0),
    (Number(b.x) || 0) + (Number(b.w) || 0),
  );
  const y2 = Math.min(
    (Number(a.y) || 0) + (Number(a.h) || 0),
    (Number(b.y) || 0) + (Number(b.h) || 0),
  );
  return Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
}

function slotAsRect(slot) {
  if (!slot) return null;
  return {
    x: Number(slot.x) || 0,
    y: Number(slot.y) || 0,
    w: Math.max(Number(slot.w) || 0, 0.001),
    h: Math.max(Number(slot.h) || 0, 0.001),
  };
}

/**
 * Resuelve el par foto+forma para Make Clipping Mask.
 * Acepta selección explícita (1+1) o infiere:
 * - solo forma → foto con asset debajo (más overlap / z)
 * - solo foto → forma clipable encima (más overlap; última marca gana empate)
 * @returns {{ slotId: string, markId: string } | null}
 */
export function resolveClippingMaskTargets(
  page,
  { slotIds = [], markIds = [] } = {},
) {
  const base = normalizeFreePage(page || {});
  const slots = base.slots || [];
  const marks = base.marks || [];
  const sIds = (slotIds || []).map(String).filter(Boolean);
  const mIds = (markIds || []).map(String).filter(Boolean);

  let slotId = sIds.length === 1 ? sIds[0] : null;
  let markId = mIds.length === 1 ? mIds[0] : null;

  if (slotId && markId) {
    const slot = slots.find((s) => String(s.id) === slotId);
    const mark = marks.find((m) => String(m.id) === markId);
    if (
      slot?.assetId != null &&
      mark &&
      CLIP_MASK_MARK_TYPES.has(String(mark.type))
    ) {
      return { slotId, markId };
    }
    return null;
  }

  if (!slotId && markId) {
    const mark = marks.find((m) => String(m.id) === markId);
    if (!mark || !CLIP_MASK_MARK_TYPES.has(String(mark.type))) return null;
    const mb = markBounds(mark);
    if (!mb) return null;
    let best = null;
    for (const s of slots) {
      if (s?.assetId == null) continue;
      const area = rectsOverlapArea(slotAsRect(s), mb);
      if (area <= 0) continue;
      const z = Number(s.z) || 0;
      if (
        !best ||
        area > best.area + 1e-9 ||
        (Math.abs(area - best.area) < 1e-9 && z >= best.z)
      ) {
        best = { id: String(s.id), area, z };
      }
    }
    return best ? { slotId: best.id, markId } : null;
  }

  if (slotId && !markId) {
    const slot = slots.find((s) => String(s.id) === slotId);
    if (!slot || slot.assetId == null) return null;
    const sb = slotAsRect(slot);
    let best = null;
    marks.forEach((m, idx) => {
      if (!m || !CLIP_MASK_MARK_TYPES.has(String(m.type))) return;
      const mb = markBounds(m);
      const area = rectsOverlapArea(sb, mb);
      if (area <= 0) return;
      if (
        !best ||
        area > best.area + 1e-9 ||
        (Math.abs(area - best.area) < 1e-9 && idx >= best.idx)
      ) {
        best = { id: String(m.id), area, idx };
      }
    });
    return best ? { slotId, markId: best.id } : null;
  }

  return null;
}

/**
 * VC / Illustrator: Object › Clipping Mask › Make
 * La marca de encima (path/forma) pasa a clip de la foto; se quita de marks.
 * Guarda pintura en clip.from para Release fiel.
 * @returns {{ slots, marks } | null}
 */
export function makeClippingMask(page, { slotId, markId } = {}) {
  const base = normalizeFreePage(page || {});
  const slots = base.slots || [];
  const marks = base.marks || [];
  const slot = slots.find((s) => String(s.id) === String(slotId));
  const mark = marks.find((m) => String(m.id) === String(markId));
  if (!slot || slot.assetId == null) return null;
  if (!mark || !CLIP_MASK_MARK_TYPES.has(String(mark.type))) return null;
  const paint = clipPaintFromMark(mark);
  const clip = pageClipToLocal(
    normalizeSlotClip(paint ? { ...mark, from: paint } : mark),
    slot,
  );
  if (!clip) return null;
  const withPaint = paint ? normalizeSlotClip({ ...clip, from: paint }) : clip;
  return {
    slots: slots.map((s) =>
      String(s.id) === String(slotId)
        ? clampSlot({
            ...s,
            clip: withPaint,
            // El marco deja de ser “máscara” por shape: el clip es la forma
            shape: "rect",
          })
        : s,
    ),
    marks: marks.filter((m) => String(m.id) !== String(markId)),
  };
}

/**
 * Object › Clipping Mask › Release — restaura la forma como marca (con pintura).
 * @returns {{ slots, marks } | null}
 */
export function releaseClippingMask(page, slotId) {
  const base = normalizeFreePage(page || {});
  const slots = base.slots || [];
  const marks = base.marks || [];
  const slot = slots.find((s) => String(s.id) === String(slotId));
  if (!slot?.clip) return null;
  const pageClip = localClipToPage(slot.clip, slot) || slot.clip;
  const from =
    slot.clip.from && typeof slot.clip.from === "object"
      ? slot.clip.from
      : null;
  const restored = clampMark({
    ...pageClip,
    id: from?.id != null ? String(from.id) : newMarkId(),
    fill: from?.fill != null ? from.fill : "none",
    stroke: from?.stroke != null ? from.stroke : 2,
    color: from?.color != null ? from.color : "#111827",
    strokeColor:
      from?.strokeColor != null
        ? from.strokeColor
        : from?.color != null
          ? from.color
          : "#111827",
    ...(from?.closed ? { closed: true } : null),
    ...(from?.opacity != null ? { opacity: from.opacity } : null),
    ...(from?.gradient ? { gradient: from.gradient } : null),
    ...(from?.role ? { role: from.role } : null),
  });
  return {
    slots: slots.map((s) =>
      String(s.id) === String(slotId)
        ? clampSlot({ ...s, clip: null, shape: "rect" })
        : s,
    ),
    marks: [...marks, restored],
  };
}

/**
 * Mueve/escala el clip en coords locales del slot (editar máscara).
 * edge: move | nw|n|ne|e|se|s|sw|w
 */
export function transformSlotClipLocal(
  slot,
  { edge = "move", dx = 0, dy = 0 } = {},
) {
  if (!slot?.clip) return slot;
  const clip = normalizeSlotClip(slot.clip);
  if (!clip || clip.type === "path") {
    // Path: solo traslación rígida
    if (clip?.type === "path" && Array.isArray(clip.points)) {
      const moved = clip.points.map((p) => ({
        x: (Number(p.x) || 0) + (Number(dx) || 0),
        y: (Number(p.y) || 0) + (Number(dy) || 0),
      }));
      return clampSlot({
        ...slot,
        clip: normalizeSlotClip({ ...clip, points: moved }),
      });
    }
    return slot;
  }
  let x = Number(clip.x) || 0;
  let y = Number(clip.y) || 0;
  let w = Math.max(0.02, Number(clip.w) || 0.02);
  let h = Math.max(0.02, Number(clip.h) || 0.02);
  const ox = x;
  const oy = y;
  const ow = w;
  const oh = h;
  const e = String(edge || "move");
  if (e === "move") {
    x += Number(dx) || 0;
    y += Number(dy) || 0;
  } else {
    if (e.includes("w")) {
      x = ox + (Number(dx) || 0);
      w = ow - (Number(dx) || 0);
    }
    if (e.includes("e")) w = ow + (Number(dx) || 0);
    if (e.includes("n")) {
      y = oy + (Number(dy) || 0);
      h = oh - (Number(dy) || 0);
    }
    if (e.includes("s")) h = oh + (Number(dy) || 0);
  }
  if (w < 0.02) {
    if (e.includes("w")) x = ox + ow - 0.02;
    w = 0.02;
  }
  if (h < 0.02) {
    if (e.includes("n")) y = oy + oh - 0.02;
    h = 0.02;
  }
  // Permitir asomar un poco fuera del marco (±0.15)
  x = Math.min(1.15, Math.max(-0.15, x));
  y = Math.min(1.15, Math.max(-0.15, y));
  w = Math.min(1.3, Math.max(0.02, w));
  h = Math.min(1.3, Math.max(0.02, h));
  return clampSlot({
    ...slot,
    clip: normalizeSlotClip({ ...clip, x, y, w, h }),
  });
}

/**
 * Geometría del clip en coords locales de la casilla (0–1 objectBoundingBox).
 * Para SVG clipPath. Acepta clip space:"local" o legacy página.
 */
export function slotClipLocalGeometry(slot) {
  if (!slot?.clip) return null;
  const clip =
    slot.clip.space === "local"
      ? normalizeSlotClip(slot.clip)
      : pageClipToLocal(slot.clip, slot);
  if (!clip) return null;
  if (
    clip.type === "path" &&
    Array.isArray(clip.points) &&
    clip.points.length
  ) {
    return {
      kind: "path",
      d: clip.points
        .map(
          (p, i) => `${i ? "L" : "M"}${Number(p.x) || 0} ${Number(p.y) || 0}`,
        )
        .join(" "),
    };
  }
  if (clip.type === "polygon" || clip.type === "star") {
    const verts =
      clip.type === "star"
        ? starPolygonPoints({
            x: clip.x,
            y: clip.y,
            w: clip.w,
            h: clip.h,
            tips: clip.tips,
            innerRatio: clip.innerRatio,
          })
        : regularPolygonPoints({
            x: clip.x,
            y: clip.y,
            w: clip.w,
            h: clip.h,
            sides: clip.sides,
          });
    const d = (verts || [])
      .map((p, i) => `${i ? "L" : "M"}${Number(p.x) || 0} ${Number(p.y) || 0}`)
      .join(" ");
    return d ? { kind: "path", d: `${d} Z` } : null;
  }
  const lx = Number(clip.x) || 0;
  const ly = Number(clip.y) || 0;
  const lw = Math.max(Number(clip.w) || 0, 0.001);
  const lh = Math.max(Number(clip.h) || 0, 0.001);
  if (clip.type === "ellipse") {
    return {
      kind: "ellipse",
      cx: lx + lw / 2,
      cy: ly + lh / 2,
      rx: Math.max(lw / 2, 0.001),
      ry: Math.max(lh / 2, 0.001),
    };
  }
  const rx =
    clip.type === "roundrect"
      ? Math.min(Number(clip.rx) || 0.04, Math.min(lw, lh) / 2)
      : 0;
  return {
    kind: "rect",
    x: lx,
    y: ly,
    w: lw,
    h: lh,
    rx,
    ry: rx,
  };
}
